import { pdfFooterHeightPx } from '../utils/pdfLetterhead';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, Typography, Stack, Select, Option, Button, Chip, Divider, Table, Sheet, Modal, ModalClose, ModalDialog, DialogTitle, DialogContent, DialogActions, Grid, Alert, Link, FormControl, FormLabel, FormHelperText } from '@mui/joy';
import { FaDownload, FaPaperPlane } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import { documentsService } from '../services/documents.service';
import { directSupabase } from '../../../lib/supabaseClient';
import { pdfExportConfigService } from '../services/pdfExportConfig.service';
import { documentTemplateService } from '../services/documentTemplate.service';
import { customerService } from '../../../services/customerService';
import type { Customer } from '../../../services/customerService';
import { accountsService } from '../../accounts/accounts.service';
import { accountAsBillToCustomer } from '../../accounts/accounts.model';
import { accountInvoicePath } from '../../accounts/components/InvoiceActionsMenu';
import { sendDocumentToCustomer } from '../services/sendDocumentToCustomer.service';
import { listDocumentShareLinks, nullifyDocumentShareLink, type DocumentShareLinkRecord } from '../services/documentShareLinks.service';
import { DocumentDatePickerModal, type InvoicePdfScenario } from '../components/DocumentDatePickerModal';
import { InvoicePaymentTermsModal } from '../components/InvoicePaymentTermsModal';
import { defaultInvoicePdfScenario, depositInvoiceLineName, depositInvoiceNarrative, documentDisplayRef, invoiceCanBeLifecycleSynced, invoiceDisplayStatus, invoicePdfDisplayGross, invoicePdfShowsDepositBreakdown, invoicePdfShowsPriorDepositReference, invoicePdfUsesOwnTotals, invoiceRoleLabel, normalizeInvoiceRole, priorDepositInvoiceAmount, priorDepositInvoiceNarrative, resolveQuotedGrossForDeposit, splitInclusiveAmount } from '../utils/invoiceLedger';
import {
  dateOnlyToIssuedAt,
  DEPOSIT_CLEARED_FUNDS_NOTE,
  formatDateGb,
  formatInvoiceDateLong,
  invoiceDueDate,
  invoicePaymentScheduleLocked,
  invoicePaymentTermsEditable,
  resolveInvoicePaymentSchedule,
  toDateOnly,
} from '../utils/invoicePaymentTerms';
import { FreeAgentSyncButton } from '../../../components/FreeAgentSyncButton';
import { getSyncStatus, getFreeAgentOrgEligibility } from '../../../integrations/freeagentSync';
import EventManager from '../../../components/EventManager';
import ImageCanvasViewer from '../../../components/ImageCanvasViewer';
import { WindowGeneratorBaySchematicSvg, WindowGeneratorThumbnail, WindowGeneratorSvg } from '../../../components/windowGeneratorRender';
import { getDesignerSummaryRows, isBayLikePayload, mergeModalStateIntoPayload } from '../components/QuoteVisualDesignerCard';
import { formatExtendersMmFromOptions } from '../utils/visualDesignerFieldFormatters';
import { isDoorType } from '../utils/visualDesignerSummary';
import { formatTrickleVentsPositionLabel, parseSemanticTricklePosition } from '../utils/trickleVentPosition';
import {
  buildLatestWindowGeneratorPayloadFromMeta,
  renderWindowGeneratorImageBlobs,
} from '../services/visualDesignerRenderAssets.service';
import { DocumentShareLinksList } from '../components/DocumentShareLinksList';
import { VisualDesignerPairingCard } from '../components/VisualDesignerPairingCard';
import { visualDesignerSurveysService, type VisualDesignerSurvey } from '../services/visualDesignerSurveys.service';
import { isVisualDesignerLine } from '../utils/visualDesignerSurveyClone';
import {
  isWorkspaceDocument,
  WORKSPACE_DOCUMENT_READ_ONLY_MESSAGE,
  workspaceDocumentPath,
} from '../utils/workspaceDocumentAccess';
import {
  buildOfflinePublicDocPreview,
  createOfflinePublicDocPreviewId,
  finalReapprovalFromSavedDelta,
  markOfflinePublicDocPreviewPending,
  offlinePublicDocPreviewHref,
  publishOfflinePublicDocPreview,
} from '../utils/offlinePublicDocPreview';
import type { DocumentWithLines, DocumentKind } from '../types/documents.types';
import { computeTotals, getAdditionalTypeLabel, getAvailableStatuses } from '../types/documents.types';
import { ensureSignedIn, GRAPH_SIGNIN_REQUIRED_TO_SEND } from '../../../utils/graphService';
import { renderDescriptionHtml } from '../utils/richTextDescription';
import { renderDocumentTemplate } from '../../../shared/pdf/documentTemplateRenderer';
import { buildSharedDocumentCss, paidWatermarkMarkup, STANDARD_DOC_LAYOUT } from '../../../shared/pdf/documentPdfStyles';
import { stripTrailingEmptyPdfPagesHtml, withPdfPrintHeightReset } from '../../../shared/pdf/prepareExportHtml';
import { buildDocumentTransmissionVersionInfo } from '../utils/transmissionInfo';
import { surveySheetDeleteConfirmMessage } from '../utils/surveySheetDelete';
import { type WorkflowTask } from '../../../components/ProjectWorkflow';
import { loadWorkspaceSnapshots } from '../../../pages/project-workspace/workspaceDashboard.service';
import type { WorkspaceSnapshot } from '../../../pages/project-workspace/workspaceDashboard.model';
import { WORKSPACE_STAGE_LIST, stateShortLabel, workspaceStageLabel } from '../../../pages/project-workspace/workspaceStages';
import { PRODUCT_TYPE_OPTIONS } from '../../../pages/project-workspace/WorkspaceProductTypesCard';
import { canProgressUnsentEstimate } from '../../../pages/project-workspace/workspaceQuote.helpers';
import { useProfile } from '../../../hooks/useProfile';
import type { Project } from '../../../types/project';
import { isDoorOnlyTypeId } from '../utils/doorTypes';
import {
  BILLING_DETAILS_REQUIRED_MESSAGE,
  BILLING_DETAILS_REQUIRED_TITLE,
  resolveDocumentCustomerIdentity,
  workspaceBillingDetailsAreValid,
} from '../../../pages/project-workspace/workspaceBillingIdentity';

interface Props {
  documentId?: string;
  embedded?: boolean;
  preferredVersionId?: string | null;
  onEdit?: (documentId: string) => void;
  onClose?: () => void;
  /** Called after a successful delete when embedded (instead of navigating to /docs). */
  onDeleted?: () => void;
  allowDelete?: boolean;
  /**
   * When set (survey workspace tabs), Delete clears that survey sheet instead of the quote document.
   */
  surveyDelete?: {
    surveyLabel: string;
    onDelete: () => Promise<void>;
    /** When set, Delete is shown but blocked (e.g. Final Survey still exists). */
    disabledReason?: string | null;
  } | null;
  /**
   * When set, Edit is shown but disabled (e.g. transmitted survey still locked — unlock first).
   */
  editDisabledReason?: string | null;
  /**
   * When set, hide the customer Send button (e.g. Final Survey tab — use Transmit for Approval instead).
   */
  hideCustomerSend?: boolean;
  /**
   * When set, the Send button uses this handler (in-app confirm) instead of DocumentDetailPage's own send.
   */
  onCustomerSend?: () => void;
  customerSendBusy?: boolean;
  onDocumentChanged?: (documentId: string) => void;
  onRequestCreateInvoice?: (documentId: string) => void;
  exportPdfSignal?: number;
  onExportPdfTriggered?: () => void;
  /** Final Survey sheet used by the offline public-doc review. Read only. */
  offlinePreviewFinalSurveyId?: string | null;
}

type ConfirmDialogState = {
  title: string;
  body: string;
  confirmLabel?: string;
  confirmColor?: 'primary' | 'danger' | 'warning' | 'success';
  onConfirm: () => void | Promise<void>;
};

type NoticeDialogState = {
  title: string;
  body: string;
  url?: string | null;
};

type LinkedProjectEventSummary = {
  event_type: string | null;
  event_date: string | null;
  email_status: string | null;
  email_sent_at: string | null;
  created_at: string | null;
};

type LinkedProjectSummary = {
  id: string;
  project_name: string | null;
  notes: string | null;
  metadata: any;
  workflow_tasks: WorkflowTask[] | null;
  supply_only: boolean | null;
  project_status: 'active' | 'completed' | 'cancelled' | null;
  status: string | null;
  install_date: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  status_changed_at: string | null;
  ordered: boolean | null;
  supplier_quote_received: boolean | null;
  deposit_received: boolean | null;
  complete: boolean | null;
  product_types: string[] | null;
};

type LinkedProjectEditDraft = {
  lifecycle_status: 'active' | 'completed' | 'cancelled';
  product_types: string[];
};

/** Board stages for the milestone strip (Completed is the last milestone). */
const STAGE_MILESTONES = WORKSPACE_STAGE_LIST.filter((stage) => stage.id !== 'cancelled');

const PROJECT_LIFECYCLE_STATUS_OPTIONS: Array<LinkedProjectEditDraft['lifecycle_status']> = ['active', 'completed', 'cancelled'];

const getLinkedProjectDraftFingerprint = (draft: LinkedProjectEditDraft): string => {
  return JSON.stringify({
    lifecycle_status: draft.lifecycle_status,
    product_types: [...(draft.product_types || [])].map(String).sort(),
  });
};

const formatLifecycleStatusLabel = (status: string | null | undefined): string => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'cancelled') return 'Cancelled';
  return 'Active';
};

const formatDateLabel = (value?: string | null): string => {
  if (!value) return '—';
  const ms = Date.parse(String(value));
  if (Number.isNaN(ms)) return String(value);
  return new Date(ms).toLocaleDateString('en-GB');
};

const formatDateTimeLabel = (value?: string | null): string => {
  if (!value) return '—';
  const ms = Date.parse(String(value));
  if (Number.isNaN(ms)) return String(value);
  return new Date(ms).toLocaleString('en-GB');
};

const parseDateMs = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = Date.parse(String(value));
  return Number.isNaN(ms) ? null : ms;
};

const pickLatestDate = (values: Array<string | null | undefined>): string | null => {
  let bestValue: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  values.forEach((value) => {
    const ms = parseDateMs(value);
    if (value && ms != null && ms > bestMs) {
      bestValue = String(value);
      bestMs = ms;
    }
  });
  return bestValue;
};

export const DocumentDetailPage: React.FC<Props> = ({
  documentId,
  embedded = false,
  preferredVersionId = null,
  onEdit,
  onClose,
  onDeleted,
  allowDelete = true,
  surveyDelete = null,
  editDisabledReason = null,
  hideCustomerSend = false,
  onCustomerSend,
  customerSendBusy = false,
  onDocumentChanged,
  onRequestCreateInvoice,
  exportPdfSignal = 0,
  onExportPdfTriggered,
  offlinePreviewFinalSurveyId = null,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = documentId || params.documentId || '';
  const [doc, setDoc] = useState<DocumentWithLines | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState<any>(null);
  const [workspaceProject, setWorkspaceProject] = useState<Project | null>(null);

  const [config, setConfig] = useState<any>(null);
  const [template, setTemplate] = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showPaymentTerms, setShowPaymentTerms] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);
  const [activeVersion, setActiveVersion] = useState<{ header: any; lines: any[] } | null>(null);
  const [syncStatus, setSyncStatus] = useState<{ syncStatus: string; syncError?: string; freeAgentUrl?: string } | null>(null);
  const [freeAgentOrgReason, setFreeAgentOrgReason] = useState<string | null>(null);
  const [invoiceQuoteDrift, setInvoiceQuoteDrift] = useState<{
    paymentStructureMismatch: boolean;
    invoicePaymentStructureApplicable: boolean;
    invoicePaymentStructurePattern: string | null;
    quotePaymentStructureApplicable: boolean;
    quotePaymentStructurePattern: string | null;
  } | null>(null);
  const [isSendingToCustomer, setIsSendingToCustomer] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [noticeDialog, setNoticeDialog] = useState<NoticeDialogState | null>(null);
  const [shareLinks, setShareLinks] = useState<DocumentShareLinkRecord[]>([]);
  const [loadingShareLinks, setLoadingShareLinks] = useState(false);
  const [nullifyingShareLinkId, setNullifyingShareLinkId] = useState<string | null>(null);
  const [customerAcceptedAt, setCustomerAcceptedAt] = useState<string | null>(null);
  const [isRegeneratingFromQuote, setIsRegeneratingFromQuote] = useState(false);
  const [invoicePdfScenario, setInvoicePdfScenario] = useState<InvoicePdfScenario>('total_without_deposit_breakdown');
  const [invoicePdfStageIndex, setInvoicePdfStageIndex] = useState<number | null>(null);
  const [priorDepositInvoice, setPriorDepositInvoice] = useState<{
    invoiceRef: string | null;
    grossTotal: number;
    depositAmount: number;
    depositPercentage: number | null;
  } | null>(null);
  const [linkedProject, setLinkedProject] = useState<LinkedProjectSummary | null>(null);
  const [linkedProjectEvents, setLinkedProjectEvents] = useState<LinkedProjectEventSummary[]>([]);
  const [linkedProjectLoading, setLinkedProjectLoading] = useState(false);
  const [linkedProjectError, setLinkedProjectError] = useState<string | null>(null);
  const [linkedProjectDraft, setLinkedProjectDraft] = useState<LinkedProjectEditDraft | null>(null);
  const [linkedProjectSaving, setLinkedProjectSaving] = useState(false);
  const [linkedProjectSaveError, setLinkedProjectSaveError] = useState<string | null>(null);
  const [linkedProjectSaveSuccess, setLinkedProjectSaveSuccess] = useState<string | null>(null);
  const [pairedSurvey, setPairedSurvey] = useState<VisualDesignerSurvey | null>(null);
  const [publicPreviewLoading, setPublicPreviewLoading] = useState(false);
  const [publicPreviewError, setPublicPreviewError] = useState<string | null>(null);
  const [publicPreviewTabNote, setPublicPreviewTabNote] = useState<string | null>(null);
  const { profile, isStaff } = useProfile();
  const pendingLinkedProjectDraftRef = React.useRef<LinkedProjectEditDraft | null>(null);
  const [zoomPreview, setZoomPreview] = useState<null | {
    kind: 'wg';
    payload: any;
    view: 'external' | 'internal';
    mode: 'design' | 'presentation';
    title: string;
  } | {
    kind: 'wg_bay_schematic';
    payload: any;
    title: string;
  } | {
    kind: 'img';
    url: string;
    title: string;
  }>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        if (!id) {
          setError('Missing document id');
          return;
        }
        console.log('Loading document:', id);
        let d = await documentsService.getDocument(id);
        console.log('Document loaded:', d?.kind, d?.id);
        if (!alive) return;
        
        // Minimal path: set document, then load PDF config and template
        console.log('Setting document state:', d?.kind);
        setDoc(d);

        // Load preferred or active version for quotations/estimates
        if (d && d.kind !== 'Invoice') {
          try {
            console.log('Loading version for:', d.kind, preferredVersionId || 'active');
            let version = null;
            if (preferredVersionId) {
              try {
                version = await documentsService.getVersion(preferredVersionId);
              } catch (err) {
                console.warn('Preferred version unavailable, falling back to active:', err);
              }
            }
            if (!version) {
              version = await documentsService.getActiveVersion(d.id);
            }
            if (!alive) return;
            if (version) {
              setActiveVersion(version);
              console.log('Version loaded:', version);
            }
          } catch (err) {
            console.warn('Failed to load document version:', err);
          }
        }

        // Load PDF configuration and template for this organisation/kind
        if (d) {
          try {
            const documentType = (d as any).kind as DocumentKind;
            const orgId = (d as any).organisationId as string;
            console.log('Loading PDF config/template for:', orgId, documentType);
            const [pdfConfig, docTemplate] = await Promise.all([
              pdfExportConfigService.getConfigByOrganisation(orgId, documentType),
              documentTemplateService.getTemplate(orgId, documentType)
            ]);
            if (!alive) return;
            setConfig(pdfConfig);
            const tpl: unknown = docTemplate?.template;
            if (typeof tpl === 'string' && tpl.length > 0) {
              setTemplate(tpl as string);
            } else {
              const fallback = await documentTemplateService.getDefaultTemplate(documentType);
              if (!alive) return;
              setTemplate(fallback);
            }
          } catch (err) {
            console.warn('Failed to load PDF configuration or template:', err);
          }
        }
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || 'Failed to load document');
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, preferredVersionId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!doc || doc.kind !== 'Invoice' || !invoicePdfUsesOwnTotals(doc.invoiceRole)) {
        if (alive) setPriorDepositInvoice(null);
        return;
      }
      try {
        const invoices = await documentsService.listLifecycleInvoices(doc.organisationId, doc.lifecycleRef);
        if (!alive) return;
        const deposit = invoices.find((invoice) => (
          normalizeInvoiceRole(invoice.invoiceRole) === 'deposit'
          && String(invoice.status || '').toLowerCase() !== 'void'
        ));
        setPriorDepositInvoice(deposit ? {
          invoiceRef: deposit.invoiceRef ?? null,
          grossTotal: Number(deposit.grossTotal || 0),
          depositAmount: Number(deposit.depositAmount || 0),
          depositPercentage: deposit.depositPercentage ?? null,
        } : null);
      } catch {
        if (alive) setPriorDepositInvoice(null);
      }
    })();
    return () => { alive = false; };
  }, [doc?.id, doc?.kind, doc?.invoiceRole, doc?.organisationId, doc?.lifecycleRef]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!doc || doc.kind !== 'Invoice' || doc.isOriginal) {
        if (alive) setInvoiceQuoteDrift(null);
        return;
      }
      try {
        const { data: quoteDoc, error: quoteErr } = await directSupabase
          .from('documents')
          .select('id, active_version_id, payment_structure_applicable, payment_structure_pattern, created_at')
          .eq('organisation_id', doc.organisationId)
          .eq('lifecycle_ref', doc.lifecycleRef)
          .eq('kind', 'Quotation')
          .neq('status', 'void')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (quoteErr) throw quoteErr;
        if (!quoteDoc) {
          if (alive) setInvoiceQuoteDrift(null);
          return;
        }

        let quotePaymentStructureApplicable = !!quoteDoc.payment_structure_applicable;
        let quotePaymentStructurePattern = quoteDoc.payment_structure_pattern != null ? String(quoteDoc.payment_structure_pattern) : null;
        if (quoteDoc.active_version_id) {
          const { data: quoteActiveVersion, error: activeErr } = await directSupabase
            .from('document_versions')
            .select('payment_structure_applicable, payment_structure_pattern')
            .eq('id', quoteDoc.active_version_id)
            .maybeSingle();
          if (activeErr) throw activeErr;
          if (quoteActiveVersion) {
            quotePaymentStructureApplicable = !!quoteActiveVersion.payment_structure_applicable;
            quotePaymentStructurePattern = quoteActiveVersion.payment_structure_pattern != null
              ? String(quoteActiveVersion.payment_structure_pattern)
              : null;
          }
        }

        const invoicePaymentStructureApplicable = !!doc.paymentStructureApplicable;
        const invoicePaymentStructurePattern = doc.paymentStructurePattern != null ? String(doc.paymentStructurePattern) : null;
        const paymentStructureMismatch =
          invoicePaymentStructureApplicable !== quotePaymentStructureApplicable ||
          (invoicePaymentStructurePattern || null) !== (quotePaymentStructurePattern || null);

        if (!alive) return;
        setInvoiceQuoteDrift({
          paymentStructureMismatch,
          invoicePaymentStructureApplicable,
          invoicePaymentStructurePattern,
          quotePaymentStructureApplicable,
          quotePaymentStructurePattern
        });
      } catch (err) {
        console.warn('Failed to compare invoice payment structure against quote', err);
        if (alive) setInvoiceQuoteDrift(null);
      }
    })();
    return () => { alive = false; };
  }, [doc]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!doc?.id) {
        if (alive) setShareLinks([]);
        return;
      }
      try {
        setLoadingShareLinks(true);
        const links = await listDocumentShareLinks(doc.id);
        if (!alive) return;
        setShareLinks(links);
      } catch (e) {
        console.warn('Failed to load document share links', e);
        if (!alive) return;
        setShareLinks([]);
      } finally {
        if (!alive) return;
        setLoadingShareLinks(false);
      }
    })();
    return () => { alive = false; };
  }, [doc?.id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!doc?.id || String(doc.status || '').toLowerCase() !== 'accepted') {
        if (alive) setCustomerAcceptedAt(null);
        return;
      }
      try {
        const { data, error } = await directSupabase
          .from('email_activity_log')
          .select('created_at, metadata')
          .filter('metadata->>documentId', 'eq', doc.id)
          .order('created_at', { ascending: true })
          .limit(200);
        if (error) throw error;

        let acceptedAt: string | null = null;
        for (const row of (data || []) as any[]) {
          const meta = (row?.metadata && typeof row.metadata === 'object') ? row.metadata : {};
          const action = String(
            (meta as any)?.customerAction?.action
            || (meta as any)?.customer_action?.action
            || ''
          ).trim().toLowerCase();
          if (action !== 'accept' && action !== 'accepted') continue;
          acceptedAt = String(
            (meta as any)?.customerAction?.at
            || (meta as any)?.customer_action?.at
            || row?.created_at
            || ''
          ).trim() || null;
          if (acceptedAt) break;
        }
        if (!alive) return;
        setCustomerAcceptedAt(acceptedAt);
      } catch {
        if (!alive) return;
        setCustomerAcceptedAt(null);
      }
    })();
    return () => { alive = false; };
  }, [doc?.id, doc?.status]);

  const loadLinkedProjectContext = useCallback(async (projectId: string, options?: { preserveProjectOnError?: boolean; silent?: boolean }) => {
    const preserveProjectOnError = !!options?.preserveProjectOnError;
    const silent = !!options?.silent;
    if (!projectId) {
      setLinkedProject(null);
      setLinkedProjectEvents([]);
      setLinkedProjectError(null);
      setLinkedProjectLoading(false);
      return;
    }
    try {
      if (!silent) setLinkedProjectLoading(true);
      setLinkedProjectError(null);
      const [{ data: projectData, error: projectError }, { data: projectEventsData, error: projectEventsError }] = await Promise.all([
        directSupabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .maybeSingle(),
        directSupabase
          .from('project_events')
          .select('event_type, event_date, email_status, email_sent_at, created_at')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false }),
      ]);
      if (projectError) throw projectError;
      if (projectEventsError) throw projectEventsError;
      setLinkedProject((projectData as LinkedProjectSummary | null) ?? null);
      setLinkedProjectEvents(((projectEventsData as LinkedProjectEventSummary[] | null) ?? []));
    } catch (e: any) {
      if (!preserveProjectOnError) setLinkedProject(null);
      setLinkedProjectEvents([]);
      setLinkedProjectError(e?.message || 'Failed to load linked project workflow summary');
    } finally {
      if (!silent) setLinkedProjectLoading(false);
    }
  }, []);

  useEffect(() => {
    const projectId = String(doc?.projectId || '').trim();
    if (!projectId) {
      setLinkedProject(null);
      setLinkedProjectEvents([]);
      setLinkedProjectError(null);
      setLinkedProjectLoading(false);
      return;
    }
    void loadLinkedProjectContext(projectId);
  }, [doc?.projectId, loadLinkedProjectContext]);

  useEffect(() => {
    if (!linkedProject) {
      setLinkedProjectDraft(null);
      setLinkedProjectSaveError(null);
      return;
    }
    setLinkedProjectDraft({
      lifecycle_status: (linkedProject.project_status || 'active') as LinkedProjectEditDraft['lifecycle_status'],
      product_types: Array.isArray(linkedProject.product_types) ? linkedProject.product_types.map(String) : [],
    });
    setLinkedProjectSaveError(null);
  }, [linkedProject]);

  const refreshShareLinks = async (documentId: string) => {
    try {
      const links = await listDocumentShareLinks(documentId);
      setShareLinks(links);
    } catch (e) {
      console.warn('Failed to refresh share links', e);
    }
  };

  // Account invoices (Finance → Invoicing) have their own page, separate from quotes and workspaces
  useEffect(() => {
    if (doc?.accountId && doc.kind === 'Invoice') {
      navigate(accountInvoicePath(doc.id), { replace: true });
    }
  }, [doc?.accountId, doc?.kind, doc?.id, navigate]);

  // Load linked customer details for template rendering (address, etc.)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (doc?.accountId) {
          // Account invoices bill a directory account (partner trade, tenant, supplier) instead of a customer
          const account = await accountsService.get(doc.accountId);
          if (!alive) return;
          if (account) setCustomerData(accountAsBillToCustomer(account));
          return;
        }
        if (!doc?.customerId) return;
        const c = await customerService.getLocalCustomerById(doc.customerId);
        if (!alive) return;
        if (c) setCustomerData(c);
      } catch (e) {
        // Non-fatal: keep going without customer data
        console.warn('Failed to load linked customer for document', e);
      }
    })();
    return () => { alive = false; };
  }, [doc?.customerId, doc?.accountId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const projectId = String(doc?.projectId || '').trim();
      if (!projectId) {
        setWorkspaceProject(null);
        return;
      }
      try {
        const { data, error: projectError } = await directSupabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .maybeSingle();
        if (!alive) return;
        if (projectError) throw projectError;
        setWorkspaceProject((data as Project) || null);
      } catch (e) {
        console.warn('Failed to load workspace for document billing identity', e);
      }
    })();
    return () => { alive = false; };
  }, [doc?.projectId]);

  const documentIdentity = useMemo(
    () => resolveDocumentCustomerIdentity({
      customer: customerData,
      project: workspaceProject,
      kind: doc?.kind,
    }),
    [customerData, workspaceProject, doc?.kind]
  );

  const syncDocId = doc?.id;
  const syncDocKind = doc?.kind;
  const syncDocOrganisationId = doc?.organisationId;

  // Load FreeAgent sync status for invoices
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!syncDocId || syncDocKind !== 'Invoice') return;
        const status = await getSyncStatus(syncDocId);
        if (!alive) return;
        setSyncStatus(status);
      } catch (e) {
        // Non-fatal: keep going without sync status
        console.warn('Failed to load FreeAgent sync status', e);
      }
    })();
    return () => { alive = false; };
  }, [syncDocId, syncDocKind]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (syncDocKind !== 'Invoice') {
        if (alive) setFreeAgentOrgReason(null);
        return;
      }
      try {
        const eligibility = await getFreeAgentOrgEligibility(syncDocOrganisationId);
        if (!alive) return;
        setFreeAgentOrgReason(eligibility.eligible ? null : (eligibility.reason || null));
      } catch (e) {
        console.warn('Failed to load FreeAgent organisation eligibility', e);
        if (alive) {
          setFreeAgentOrgReason('FreeAgent only accepts Keystone Glazing and Keystone South UK invoices. This invoice cannot be transmitted until it belongs to one of them.');
        }
      }
    })();
    return () => { alive = false; };
  }, [syncDocKind, syncDocOrganisationId]);

  useEffect(() => {
    if (!doc || doc.kind === 'Invoice' || !activeVersion?.header) {
      setPairedSurvey(null);
      return;
    }
    const projectId = String(doc.projectId || '').trim();
    const versionId = String(activeVersion.header.id || '').trim();
    const surveyId = String(activeVersion.header.visual_survey_id || activeVersion.header.visualSurveyId || '').trim();
    if (!projectId) {
      setPairedSurvey(null);
      return;
    }
    let cancelled = false;
    visualDesignerSurveysService
      .listByProject(projectId)
      .then((list) => {
        if (cancelled) return;
        const bound = list.find((survey) => (surveyId && survey.id === surveyId) || survey.documentVersionId === versionId) || null;
        setPairedSurvey(bound);
      })
      .catch(() => {
        if (!cancelled) setPairedSurvey(null);
      });
    return () => {
      cancelled = true;
    };
  }, [doc?.id, doc?.kind, doc?.projectId, activeVersion?.header?.id, activeVersion?.header?.visual_survey_id, activeVersion?.header?.visualSurveyId]);

  // Merge active version header/lines over the document frame for read-only rendering
  const viewDoc = useMemo(() => {
    if (!doc) return null;

    // Invoices are versionless: use the base document directly
    if (doc.kind === 'Invoice') {
      return doc;
    }

    // For quotes/estimates: require an active version
    if (!activeVersion?.header) {
      return null;
    }

    const h = activeVersion.header;
    const mappedLines = (activeVersion.lines || []).map((l: any) => ({
      id: l.id,
      lineKind: l.line_kind,
      additionalType: l.additional_type ?? null,
      materialId: l.material_id,
      equipmentId: l.equipment_id,
      labourRateId: l.labour_rate_id,
      name: l.name,
      description: l.description,
      qty: l.qty,
      unit: l.unit,
      unitPrice: l.unit_price,
      vatInclusive: l.vat_inclusive,
      lineTotal: l.line_total,
      sortOrder: l.sort_order,
      labourDays: l.labour_days,
      labourHours: l.labour_hours,
      metaJson: l.meta_json ?? null,
    }));
    
    const versionDescription = String(h.description ?? '').trim();
    const documentDescription = String(doc.description ?? '').trim();

    return {
      ...doc,
      title: h.title ?? doc.title,
      // Prefer version text, but fall back when the active version was saved with an empty string.
      description: versionDescription || documentDescription || '',
      fees: {
        adminPct: h.admin_pct ?? 0,
        adminCap: h.admin_cap ?? 0,
        contingencyPct: h.contingency_pct ?? 0,
        profitPct: h.profit_pct ?? 0,
        developerPct: null,
      },
      vatEnabled: h.vat_enabled ?? false,
      vatPct: h.vat_pct ?? 0.2,
      inputVatSelected: h.input_vat_selected ?? false,
      hasDeposit: h.has_deposit ?? false,
      depositAmount: h.deposit_amount ?? 0,
      depositPercentage: h.deposit_percentage ?? null,
      projectDays: h.project_days ?? null,
      projectHours: h.project_hours ?? null,
      issuedAt: h.issued_at ?? null,
      paymentStructureApplicable: !!h.payment_structure_applicable,
      paymentStructurePattern: h.payment_structure_pattern ?? null,
      lines: mappedLines,
    } as any;
  }, [doc, activeVersion]);

  const depositNarrative = useMemo(() => {
    if (!viewDoc || viewDoc.kind !== 'Invoice' || normalizeInvoiceRole(viewDoc.invoiceRole) !== 'deposit') {
      return null;
    }
    return depositInvoiceNarrative({
      lifecycleRef: viewDoc.lifecycleRef,
      quotedGross: resolveQuotedGrossForDeposit({
        depositAmount: viewDoc.depositAmount,
        depositPercentage: viewDoc.depositPercentage,
      }),
      sourceKind: 'Quotation',
    });
  }, [viewDoc]);

  const descriptionHtml = useMemo(
    () => renderDescriptionHtml(depositNarrative || viewDoc?.description),
    [depositNarrative, viewDoc?.description],
  );

  const transmissionVersionInfo = useMemo(() => {
    if (!doc || doc.kind === 'Invoice') return null;
    const activeVersionId = String((activeVersion as any)?.header?.id || '').trim() || null;
    const activeVersionNumberRaw = Number((activeVersion as any)?.header?.version_number);
    const activeVersionNumber = Number.isFinite(activeVersionNumberRaw) ? Math.trunc(activeVersionNumberRaw) : null;
    return buildDocumentTransmissionVersionInfo({
      activeVersionId,
      activeVersionNumber,
      activeIsTransmitted: !!(activeVersion as any)?.header?.is_transmitted,
      activeOverrideUnlocked: !!(activeVersion as any)?.header?.override_unlocked,
      shareLinks: shareLinks || [],
    });
  }, [doc, activeVersion, shareLinks]);

  const linkedProjectWorkflowSummary = useMemo(() => {
    if (!linkedProject) return null;
    const workflowTasks = Array.isArray(linkedProject.workflow_tasks) ? linkedProject.workflow_tasks : [];
    const workflowTaskDateMap = new Map<string, string>();
    workflowTasks.forEach((task) => {
      if (!task?.completed) return;
      if (!task?.date) return;
      workflowTaskDateMap.set(String(task.task || '').trim(), String(task.date));
    });
    const eventDateByType = new Map<string, string[]>();
    linkedProjectEvents.forEach((event) => {
      const key = String(event.event_type || '').trim();
      if (!key || !event.event_date) return;
      const bucket = eventDateByType.get(key) || [];
      bucket.push(String(event.event_date));
      eventDateByType.set(key, bucket);
    });

    const latestByEventType = (types: string[]): string | null => {
      const allDates = types.flatMap((type) => eventDateByType.get(type) || []);
      return pickLatestDate(allDates);
    };

    const surveyDate = pickLatestDate([
      workflowTaskDateMap.get('Final Survey Complete') || workflowTaskDateMap.get('Second Survey Complete') || null,
      workflowTaskDateMap.get('First Survey Complete') || null,
      latestByEventType(['finalSurveyAppointmentConfirmation', 'firstSurveyAppointmentConfirmation']),
    ]);
    const firstSurveyDate = pickLatestDate([
      latestByEventType(['firstSurveyAppointmentConfirmation']),
      workflowTaskDateMap.get('First Survey Complete') || null,
    ]);
    const finalSurveyDate = pickLatestDate([
      latestByEventType(['finalSurveyAppointmentConfirmation']),
      workflowTaskDateMap.get('Final Survey Complete') || workflowTaskDateMap.get('Second Survey Complete') || null,
    ]);
    const installationOrDeliveryDate = pickLatestDate([
      latestByEventType(['installationDateConfirmation', 'deliveryDateConfirmation']),
      workflowTaskDateMap.get('Schedule Installation Date') || null,
      workflowTaskDateMap.get('Schedule Delivery Date') || null,
    ]);
    const orderDate = workflowTaskDateMap.get('Order Goods') || null;
    const installDate = pickLatestDate([
      linkedProject.install_date,
      workflowTaskDateMap.get('Schedule Installation Date') || null,
      workflowTaskDateMap.get('Schedule Delivery Date') || null,
      latestByEventType(['installationDateConfirmation', 'deliveryDateConfirmation']),
    ]);
    const completionDate = pickLatestDate([
      linkedProject.completed_at,
      workflowTaskDateMap.get('Complete Project') || null,
      latestByEventType(['completionEmail']),
    ]);

    const statusCounts = linkedProjectEvents.reduce<Record<string, number>>((acc, event) => {
      const key = String(event.email_status || '').trim().toLowerCase();
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const lastEmailEvent = [...linkedProjectEvents]
      .filter((event) => !!(event.email_sent_at || event.event_date || event.created_at))
      .sort((a, b) => {
        const aMs = parseDateMs(a.email_sent_at || a.event_date || a.created_at) ?? 0;
        const bMs = parseDateMs(b.email_sent_at || b.event_date || b.created_at) ?? 0;
        return bMs - aMs;
      })[0];

    const notes = String(linkedProject.notes || '').trim() || null;
    const internalNotes = String(
      linkedProject?.metadata?.internal_notes
      || linkedProject?.metadata?.internalNotes
      || linkedProject?.metadata?.notes_internal
      || ''
    ).trim() || null;

    return {
      surveyDate,
      firstSurveyDate,
      finalSurveyDate,
      installationOrDeliveryDate,
      orderDate,
      installDate,
      completionDate,
      orderedFlag: linkedProject.ordered,
      emailStatusSummary: statusCounts,
      lastEmailEvent,
      notes,
      internalNotes,
    };
  }, [linkedProject, linkedProjectEvents]);

  const workspaceReadOnly = !embedded && isWorkspaceDocument(doc);
  const workspacePath = workspaceDocumentPath(doc);

  const canEditLinkedProject = useMemo(() => {
    const role = String(profile?.role || '').trim().toLowerCase();
    return isStaff && role !== 'document' && !workspaceReadOnly;
  }, [isStaff, profile?.role, workspaceReadOnly]);

  const customerForEventManager = useMemo<Customer | null>(() => {
    if (!customerData && !doc) return null;
    const customerId = String(customerData?.id || doc?.customerId || '').trim();
    if (!customerId) return null;
    const createdAt = String(customerData?.created_at || doc?.createdAt || new Date().toISOString());
    const updatedAt = String(customerData?.updated_at || doc?.updatedAt || createdAt);
    return {
      id: customerId,
      name: String(customerData?.name || `${doc?.customerFirstName || ''} ${doc?.customerLastName || ''}`.trim() || 'Customer'),
      email: customerData?.email ?? null,
      phone: String(customerData?.phone || ''),
      address: String(customerData?.address || ''),
      company: customerData?.company || undefined,
      created_at: createdAt,
      updated_at: updatedAt,
      source: (customerData?.source === 'api' || customerData?.source === 'local') ? customerData.source : 'local',
    };
  }, [customerData, doc]);

  // Where the linked workspace is: the same answer as the Board, from its records
  const [linkedSnapshot, setLinkedSnapshot] = useState<WorkspaceSnapshot | null>(null);
  useEffect(() => {
    if (!linkedProject?.id) {
      setLinkedSnapshot(null);
      return;
    }
    let cancelled = false;
    loadWorkspaceSnapshots([linkedProject as any])
      .then((snapshots) => {
        if (!cancelled) setLinkedSnapshot(snapshots[linkedProject.id] || null);
      })
      .catch((err) => console.warn('Failed to load workspace stage', err));
    return () => {
      cancelled = true;
    };
  }, [linkedProject]);
  const workflowRiver = useMemo(() => {
    if (!linkedProject) return null;
    const milestones = STAGE_MILESTONES.map((stage) => stage.label);
    const isCancelled = linkedSnapshot?.stage === 'cancelled'
      || String(linkedProject.project_status || '').toLowerCase() === 'cancelled';
    const activeIndex = isCancelled
      ? milestones.length - 1
      : STAGE_MILESTONES.findIndex((stage) => stage.id === linkedSnapshot?.stage);
    return { milestones, activeIndex, isCancelled };
  }, [linkedProject, linkedSnapshot]);

  const updateLinkedProjectDraft = (patch: Partial<LinkedProjectEditDraft>) => {
    let nextDraftForSave: LinkedProjectEditDraft | null = null;
    setLinkedProjectDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      if (getLinkedProjectDraftFingerprint(next) === getLinkedProjectDraftFingerprint(prev)) {
        return prev;
      }
      nextDraftForSave = next;
      return next;
    });
    if (nextDraftForSave) {
      setLinkedProjectSaveError(null);
      setLinkedProjectSaveSuccess(null);
      void saveLinkedProjectDraft(nextDraftForSave);
    }
  };

  const validateLinkedProjectDraft = (draft: LinkedProjectEditDraft): string | null => {
    if (!PROJECT_LIFECYCLE_STATUS_OPTIONS.includes(draft.lifecycle_status)) {
      return 'Lifecycle status is invalid.';
    }
    const allowedProductTypes = new Set(PRODUCT_TYPE_OPTIONS);
    const normalized = Array.isArray(draft.product_types) ? draft.product_types : [];
    if (normalized.some((value) => !allowedProductTypes.has(value as any))) {
      return 'One or more product types are invalid.';
    }
    if (normalized.length > PRODUCT_TYPE_OPTIONS.length) {
      return 'Too many product types selected.';
    }
    return null;
  };

  const buildLifecyclePatch = (
    previous: Pick<LinkedProjectSummary, 'project_status' | 'completed_at' | 'cancelled_at'>,
    nextStatus: 'active' | 'completed' | 'cancelled'
  ): { status_changed_at?: string; completed_at?: string | null; cancelled_at?: string | null } => {
    const nowIso = new Date().toISOString();
    const prevStatus = (previous.project_status || 'active') as 'active' | 'completed' | 'cancelled';
    const statusChanged = prevStatus !== nextStatus;
    const patch: { status_changed_at?: string; completed_at?: string | null; cancelled_at?: string | null } = {};
    if (statusChanged) patch.status_changed_at = nowIso;
    if (nextStatus === 'completed') {
      patch.completed_at = previous.completed_at || nowIso;
      patch.cancelled_at = null;
      return patch;
    }
    if (nextStatus === 'cancelled') {
      patch.cancelled_at = previous.cancelled_at || nowIso;
      patch.completed_at = null;
      return patch;
    }
    patch.completed_at = null;
    patch.cancelled_at = null;
    return patch;
  };

  const saveLinkedProjectDraft = async (draftOverride?: LinkedProjectEditDraft) => {
    const draftToSave = draftOverride || linkedProjectDraft;
    if (!linkedProject || !draftToSave || !canEditLinkedProject) return;
    if (linkedProjectSaving) {
      pendingLinkedProjectDraftRef.current = draftToSave;
      return;
    }
    const validationError = validateLinkedProjectDraft(draftToSave);
    if (validationError) {
      setLinkedProjectSaveError(validationError);
      return;
    }
    const previous = linkedProject;
    const nextLifecycleStatus: 'active' | 'completed' | 'cancelled' = draftToSave.lifecycle_status;
    const lifecyclePatch = buildLifecyclePatch(previous, nextLifecycleStatus);
    const payload: Partial<LinkedProjectSummary> = {
      project_status: nextLifecycleStatus,
      product_types: draftToSave.product_types,
      ...lifecyclePatch,
    };

    setLinkedProjectSaving(true);
    setLinkedProjectSaveError(null);
    setLinkedProjectSaveSuccess(null);
    setLinkedProject((prev) => (prev ? { ...prev, ...payload } : prev));
    try {
      const { data, error } = await directSupabase
        .from('projects')
        .update(payload)
        .eq('id', linkedProject.id)
        .select('*')
        .single();
      if (error) throw error;
      setLinkedProject((data as LinkedProjectSummary) || null);
      setLinkedProjectSaveSuccess('Linked project saved.');
      const projectId = String(linkedProject.id || '').trim();
      if (projectId) {
        void loadLinkedProjectContext(projectId, { preserveProjectOnError: true, silent: true });
        setTimeout(() => {
          void loadLinkedProjectContext(projectId, { preserveProjectOnError: true, silent: true });
        }, 1200);
      }
    } catch (e: any) {
      setLinkedProject(previous);
      setLinkedProjectSaveError(e?.message || 'Failed to update linked project.');
    } finally {
      setLinkedProjectSaving(false);
      const pending = pendingLinkedProjectDraftRef.current;
      pendingLinkedProjectDraftRef.current = null;
      if (pending && getLinkedProjectDraftFingerprint(pending) !== getLinkedProjectDraftFingerprint(draftToSave)) {
        void saveLinkedProjectDraft(pending);
      }
    }
  };

  const handleExportPdf = () => {
    if (!viewDoc) return;
    // For invoices, allow choosing the export variant without modifying the invoice itself.
    if (viewDoc.kind === 'Invoice') {
      setInvoicePdfScenario(defaultInvoicePdfScenario((viewDoc as any)?.invoiceRole));
    }
    setShowDatePicker(true);
  };

  const openExportPdfRef = React.useRef(handleExportPdf);
  openExportPdfRef.current = handleExportPdf;

  useEffect(() => {
    if (!embedded || !exportPdfSignal || !viewDoc?.id) return;
    openExportPdfRef.current();
    onExportPdfTriggered?.();
  }, [embedded, exportPdfSignal, viewDoc?.id, onExportPdfTriggered]);

  if (loading) return <Typography>Loading…</Typography>;
  if (error) return <Typography color="danger">{error}</Typography>;
  if (!doc) return <Typography>No document found.</Typography>;
  if (!viewDoc) return <Typography>Loading document details…</Typography>;

  const totals = computeTotals(
    (viewDoc as DocumentWithLines).lines.map((l: any) => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal, vatInclusive: l.vatInclusive })),
    (viewDoc as DocumentWithLines).fees,
    { enabled: (viewDoc as DocumentWithLines).vatEnabled, pct: (viewDoc as DocumentWithLines).vatPct }
  );

  const isVisualDesignerLine = (line: any) => (line as any)?.metaJson?.feature === 'visual_designer';
  const visualDesignerLinesUi = ((viewDoc as any)?.lines || []).filter((l: any) => isVisualDesignerLine(l));
  const pricingLinesUi = ((viewDoc as any)?.lines || []).filter((l: any) => !isVisualDesignerLine(l));

  const toPublicImageUrl = (path?: string): string => {
    if (!path) return '';
    const p = String(path);
    if (p.startsWith('http://') || p.startsWith('https://')) return p;
    if (p.startsWith('/')) return p;
    return `/${p}`;
  };

  // Debug logging for totals mismatch
  console.log('🔍 DocumentDetailPage totals debug:', {
    lines: (viewDoc as DocumentWithLines).lines.map((l: any) => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal })),
    fees: (viewDoc as DocumentWithLines).fees,
    vatEnabled: (viewDoc as DocumentWithLines).vatEnabled,
    vatPct: (viewDoc as DocumentWithLines).vatPct,
    totals: totals
  });
  
  // Calculate additional totals needed for display
  const subtotal = totals.preProfit + totals.profit;
  const computedVatAmount = viewDoc.vatEnabled ? subtotal * viewDoc.vatPct : 0;
  const invoiceRole = normalizeInvoiceRole(viewDoc.invoiceRole);
  const isRoleInvoice = viewDoc.kind === 'Invoice' && invoiceRole != null;
  const invoiceDatesLocked = invoicePaymentScheduleLocked({ freeAgentUrl: syncStatus?.freeAgentUrl });
  const invoiceSchedule = viewDoc.kind === 'Invoice'
    ? resolveInvoicePaymentSchedule({
      invoiceRole: viewDoc.invoiceRole,
      issuedAt: viewDoc.issuedAt,
      createdAt: viewDoc.createdAt,
      paymentTermsInDays: viewDoc.paymentTermsInDays,
      locked: invoiceDatesLocked,
    })
    : null;
  const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
  const computedNet = r2(subtotal);
  const computedVat = r2(viewDoc.vatEnabled ? computedNet * viewDoc.vatPct : 0);
  const computedGross = r2(totals.grandTotal);
  const storedNet = r2(Number((viewDoc as any).netTotal ?? computedNet));
  const storedVat = r2(Number((viewDoc as any).vatAmount ?? computedVatAmount));
  const storedGross = r2(Number((viewDoc as any).grossTotal ?? totals.grandTotal));
  const invoiceTotalsDrift = (() => {
    if (viewDoc.kind !== 'Invoice') return null;
    const hasStored =
      (viewDoc as any).netTotal != null ||
      (viewDoc as any).vatAmount != null ||
      (viewDoc as any).grossTotal != null;
    if (!hasStored) return null;
    const deltaNet = r2(Math.abs(storedNet - computedNet));
    const deltaVat = r2(Math.abs(storedVat - computedVat));
    const deltaGross = r2(Math.abs(storedGross - computedGross));
    const hasDrift = deltaNet > 0.01 || deltaVat > 0.01 || deltaGross > 0.01;
    if (!hasDrift) return null;
    return {
      stored: { net: storedNet, vat: storedVat, gross: storedGross },
      computed: { net: computedNet, vat: computedVat, gross: computedGross },
      delta: { net: deltaNet, vat: deltaVat, gross: deltaGross }
    };
  })();
  // Role invoices are a VAT-inclusive cash amount (deposit / balance / etc).
  // Stored public-doc snapshots used to write gross into net and leave VAT at 0.
  const roleInvoiceSplit = isRoleInvoice
    ? splitInclusiveAmount(
        invoiceRole === 'deposit' && Number(viewDoc.depositAmount || 0) > 0
          ? Number(viewDoc.depositAmount)
          : (Number((viewDoc as any).grossTotal || 0) || computedGross),
        !!viewDoc.vatEnabled,
        Number(viewDoc.vatPct || 0),
      )
    : null;
  const netTotal = roleInvoiceSplit?.net ?? (invoiceTotalsDrift ? computedNet : ((viewDoc as any).netTotal ?? subtotal));
  const vatAmount = roleInvoiceSplit?.vat ?? (invoiceTotalsDrift ? computedVat : ((viewDoc as any).vatAmount ?? computedVatAmount));
  const grandTotal = roleInvoiceSplit?.gross ?? (invoiceTotalsDrift ? computedGross : ((viewDoc as any).grossTotal ?? totals.grandTotal));

  const goodsSubtotal = totals.materialsTotal + totals.equipmentTotal + totals.otherTotal;
  const labourSubtotal = totals.labourTotal;
  const goodsIncVat = viewDoc.vatEnabled ? goodsSubtotal * (1 + viewDoc.vatPct) : goodsSubtotal;
  const labourIncVat = viewDoc.vatEnabled ? labourSubtotal * (1 + viewDoc.vatPct) : labourSubtotal;
  // Profit (after labour deducted) matches edit view: admin + contingency + profit margin on base cost
  const baseProfitOnly = totals.baseCost * (viewDoc.fees.profitPct ?? 0);
  const profitDisplay = r2(totals.adminFee + totals.contingency + baseProfitOnly);
  
  const hasSpecifiedDuration = Boolean(
    (viewDoc.projectDays != null && viewDoc.projectDays > 0) ||
    (viewDoc.projectHours != null && viewDoc.projectHours > 0)
  );
  const effectiveDays = viewDoc.projectDays && viewDoc.projectDays > 0
    ? Math.ceil(viewDoc.projectDays)
    : 1;
  const profitPerDay = hasSpecifiedDuration ? r2(profitDisplay / effectiveDays) : null;
  const profitPerDayLabel = `Profit Per Day (${effectiveDays} day${effectiveDays === 1 ? '' : 's'})`;

  // Calculate materials excluding VAT (for materials that have VAT inclusive flag)
  const materialsExcVat = (viewDoc as DocumentWithLines).lines
    .filter((l: any) => l.lineKind === 'material')
    .reduce((acc: number, l: any) => {
      const lineTotal = l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0);
      // If vatInclusive is true, remove VAT from the line total
      const isVatInclusive = (l as any).vatInclusive === true;
      const excVatAmount = isVatInclusive && (viewDoc as DocumentWithLines).vatEnabled 
        ? lineTotal / (1 + (viewDoc as DocumentWithLines).vatPct)  // Remove VAT from inclusive price
        : lineTotal; // Already net price
      return acc + excVatAmount;
    }, 0);

  // Calculate labour excluding VAT (labour is always recorded as net)
  const labourExcVat = (viewDoc as DocumentWithLines).lines
    .filter((l: any) => l.lineKind === 'labour')
    .reduce((acc: number, l: any) => {
      const lineTotal = l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0);
      return acc + lineTotal; // Labour is always net
    }, 0);

  const money = (amount: number) => `£${amount.toFixed(2)}`;
  const percent = (pct: number) => `${(pct * 100).toFixed(0)}%`;

  // Deposit / outstanding breakdown helpers
  const depositGross = Number(viewDoc.depositAmount || 0);
  const depositVatPortion = viewDoc.vatEnabled
    ? r2(depositGross * (viewDoc.vatPct / (1 + viewDoc.vatPct)))
    : 0;
  const depositNet = r2(depositGross - depositVatPortion);

  const outstandingGross = r2(Math.max(0, Number(grandTotal || 0) - depositGross));
  const outstandingVatPortion = viewDoc.vatEnabled
    ? r2(outstandingGross * (viewDoc.vatPct / (1 + viewDoc.vatPct)))
    : 0;
  const outstandingNet = r2(outstandingGross - outstandingVatPortion);

  // Payment structure breakdown (mirrors edit view logic)
  const paymentStructures: Array<{ label: string; parts: number[] }> = [
    { label: '20/20/30/30', parts: [20, 20, 30, 30] },
    { label: '25/50/25', parts: [25, 50, 25] },
    { label: '30/30/40', parts: [30, 30, 40] },
    { label: '30/40/30', parts: [30, 40, 30] },
    { label: '40/30/30', parts: [40, 30, 30] },
    { label: '40/40/20', parts: [40, 40, 20] },
    { label: '50/25/25', parts: [50, 25, 25] },
    { label: '50/40/10', parts: [50, 40, 10] },
  ];

  const activePaymentStructure = (viewDoc as any).paymentStructureApplicable && (viewDoc as any).paymentStructurePattern
    ? paymentStructures.find(s => s.label === (viewDoc as any).paymentStructurePattern) || null
    : null;
  const invoiceStageOptions = activePaymentStructure
    ? activePaymentStructure.parts.map((pct, index) => ({
        index,
        pct,
        label: `Stage ${index + 1}`
      }))
    : [];

  const remainingAfterDeposit = outstandingGross;
  const isDepositInvoice = invoiceRole === 'deposit';
  const showOutstandingBreakdown = !isDepositInvoice && !invoicePdfUsesOwnTotals(invoiceRole);

  // Function to render template with real data
  const renderTemplate = async (
    templateHtml: string,
    documentDate?: string,
    invoiceScenario?: InvoicePdfScenario | null,
    invoiceStageIndex?: number | null,
    opts?: { vdObjectUrls?: string[]; skipLiveV2PreviewRender?: boolean }
  ) => {
    if (!templateHtml) return '';

    let resolvedPriorDeposit = priorDepositInvoice;
    if (!resolvedPriorDeposit && viewDoc.kind === 'Invoice' && invoicePdfUsesOwnTotals(viewDoc.invoiceRole)) {
      try {
        const invoices = await documentsService.listLifecycleInvoices(viewDoc.organisationId, viewDoc.lifecycleRef);
        const deposit = invoices.find((invoice) => (
          normalizeInvoiceRole(invoice.invoiceRole) === 'deposit'
          && String(invoice.status || '').toLowerCase() !== 'void'
        ));
        if (deposit) {
          resolvedPriorDeposit = {
            invoiceRef: deposit.invoiceRef ?? null,
            grossTotal: Number(deposit.grossTotal || 0),
            depositAmount: Number(deposit.depositAmount || 0),
            depositPercentage: deposit.depositPercentage ?? null,
          };
        }
      } catch {
        resolvedPriorDeposit = null;
      }
    }
    
    // Invoices use the issue schedule. Other docs keep issuedAt / created_at.
    const invoiceDateForPdf = invoiceSchedule
      ? (toDateOnly(documentDate) || invoiceSchedule.invoiceDate)
      : null;
    const dueDateForPdf = invoiceSchedule && invoiceDateForPdf
      ? invoiceDueDate(invoiceDateForPdf, invoiceSchedule.paymentTermsInDays)
      : null;
    const effectiveDate = invoiceDateForPdf
      ? dateOnlyToIssuedAt(invoiceDateForPdf)
      : (documentDate || viewDoc.issuedAt || viewDoc.createdAt);
    
    // Derive customer title/name, preferring live customer directory data when available.
    // Guard against bad data where title is accidentally populated with a full name
    // (e.g. "James Wells"), which can duplicate names in salutations.
    const normalizeToken = (value: string): string =>
      String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s.-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const rawCustomerTitle = String(customerData?.title || viewDoc.customerTitle || '').trim();
    const firstNameNorm = normalizeToken(String(viewDoc.customerFirstName || '').trim());
    const lastNameNorm = normalizeToken(String(viewDoc.customerLastName || '').trim());
    const fullNameNorm = normalizeToken([viewDoc.customerFirstName, viewDoc.customerLastName].filter(Boolean).join(' '));
    const directoryFullNameNorm = normalizeToken(String(customerData?.name || '').trim());
    const titleNorm = normalizeToken(rawCustomerTitle);
    const titleLooksLikeName = !!titleNorm && (
      (fullNameNorm && titleNorm === fullNameNorm) ||
      (directoryFullNameNorm && titleNorm === directoryFullNameNorm) ||
      (firstNameNorm && titleNorm === firstNameNorm) ||
      (lastNameNorm && titleNorm === lastNameNorm) ||
      (firstNameNorm && titleNorm.includes(firstNameNorm) && titleNorm.split(' ').length > 1) ||
      (lastNameNorm && titleNorm.includes(lastNameNorm) && titleNorm.split(' ').length > 1) ||
      (directoryFullNameNorm && titleNorm.includes(directoryFullNameNorm))
    );
    const derivedCustomerTitle = titleLooksLikeName ? '' : rawCustomerTitle;
    const derivedLastName = (() => {
      // Account invoices address a business, so use the whole company name
      if (viewDoc.accountId) return (customerData?.name || '').trim() || 'Customer';
      if (viewDoc.customerLastName) return viewDoc.customerLastName;
      const n = (customerData?.name || '').trim();
      if (!n) return 'Customer';
      const parts = n.split(/\s+/);
      return parts[parts.length - 1] || n;
    })();

    // Prepare data for template
    const isVisualDesignerLine = (line: any) => (line as any)?.metaJson?.feature === 'visual_designer';
    const allDocLines = (viewDoc as DocumentWithLines).lines || [];
    const pricingLines = allDocLines.filter((l: any) => !isVisualDesignerLine(l));
    const visualDesignerLines = allDocLines.filter((l: any) => isVisualDesignerLine(l));
    // Back-compat: many existing templates key major sections off {{#hasLineItems}}.
    // If a document is visual-designer-only, keep that section truthy so core
    // content blocks (that historically sat inside it) still render in export.
    const exportLineItems = pricingLines.length > 0 ? pricingLines : allDocLines;

    const scenario = viewDoc.kind === 'Invoice' ? String(invoiceScenario || '').trim() : '';
    const depositOnly = viewDoc.kind === 'Invoice' && scenario === 'deposit_only';
    const forceNoVatNoDeposit = viewDoc.kind === 'Invoice' && scenario === 'no_vat_no_deposit';
    const outstandingOnly = viewDoc.kind === 'Invoice' && scenario === 'outstanding_only';
    const stageOnlyRequested = viewDoc.kind === 'Invoice' && scenario === 'stage_only';

    const siblingDepositAmount = priorDepositInvoiceAmount(resolvedPriorDeposit);
    const siblingDepositPercentage = resolvedPriorDeposit?.depositPercentage ?? viewDoc.depositPercentage ?? null;
    const templateDepositAmount = invoicePdfUsesOwnTotals(viewDoc.invoiceRole) && siblingDepositAmount > 0
      ? siblingDepositAmount
      : Number(viewDoc.depositAmount || 0);
    const templateDepositPercentage = invoicePdfUsesOwnTotals(viewDoc.invoiceRole) && siblingDepositAmount > 0
      ? siblingDepositPercentage
      : viewDoc.depositPercentage;
    const hasDepositFiguresDoc = templateDepositAmount > 0 && (
      (!!viewDoc.hasDeposit && Number(viewDoc.depositAmount || 0) > 0)
      || (invoicePdfUsesOwnTotals(viewDoc.invoiceRole) && siblingDepositAmount > 0)
    );
    const effectiveVatEnabled = forceNoVatNoDeposit ? false : !!viewDoc.vatEnabled;
    const effectiveHasDeposit = invoicePdfShowsDepositBreakdown({
      kind: viewDoc.kind,
      invoiceRole: viewDoc.invoiceRole,
      scenario,
    }) && hasDepositFiguresDoc;
    const priorDepositNarrative = invoicePdfShowsPriorDepositReference({
      kind: viewDoc.kind,
      invoiceRole: viewDoc.invoiceRole,
      scenario,
    })
      ? priorDepositInvoiceNarrative({
          amount: siblingDepositAmount,
          invoiceRef: resolvedPriorDeposit?.invoiceRef,
          depositPercentage: siblingDepositPercentage,
        })
      : '';
    const effectiveNoDeposit = !effectiveHasDeposit;
    const selectedStageIndex = invoiceStageIndex != null ? Number(invoiceStageIndex) : null;
    const hasValidStageSelection =
      stageOnlyRequested &&
      !!activePaymentStructure &&
      selectedStageIndex != null &&
      selectedStageIndex >= 0 &&
      selectedStageIndex < activePaymentStructure.parts.length;
    const stageOnly = !!hasValidStageSelection;
    const suppressOtherTotalsBlocks = depositOnly || outstandingOnly || stageOnly;
    const hasDepositForBlocks = suppressOtherTotalsBlocks ? false : effectiveHasDeposit;
    const noDepositForBlocks = suppressOtherTotalsBlocks ? false : effectiveNoDeposit;

    const depositGross = templateDepositAmount;
    const vatRate = Number(viewDoc.vatPct || 0);
    const depositExVatValue = (effectiveVatEnabled && vatRate > 0) ? (depositGross / (1 + vatRate)) : depositGross;
    const depositVatValue = (effectiveVatEnabled && vatRate > 0) ? Math.max(depositGross - depositExVatValue, 0) : 0;

    const outstanding = invoicePdfDisplayGross({
      invoiceRole: viewDoc.invoiceRole,
      scenario: outstandingOnly ? 'outstanding_only' : scenario,
      grandTotal,
      depositAmount: viewDoc.depositAmount,
    });
    const stageComputation = (() => {
      const fallback = {
        label: '',
        pct: 0,
        amountInclVat: 0,
        amountExVat: 0,
        amountVat: 0,
        previouslyInvoicedInclVat: 0,
        remainingAfterThisStageInclVat: Number(grandTotal || 0)
      };
      if (!stageOnly || !activePaymentStructure || selectedStageIndex == null) return fallback;
      const parts = activePaymentStructure.parts;
      const contractTotal = Number(grandTotal || 0);
      const depositPctRaw = Number(parts[0] || 0);
      const depositGrossByPattern = contractTotal * (depositPctRaw / 100);
      const remainingAfterPatternDeposit = Math.max(contractTotal - depositGrossByPattern, 0);
      const stagePct = Number(parts[selectedStageIndex] || 0);
      let stageAmountInclVat = 0;
      if (selectedStageIndex === 0) {
        stageAmountInclVat = depositGrossByPattern;
      } else {
        const remainingParts = parts.slice(1);
        const sumRemaining = remainingParts.reduce((acc, value) => acc + Number(value || 0), 0);
        const stageShare = sumRemaining > 0 ? stagePct / sumRemaining : 0;
        stageAmountInclVat = remainingAfterPatternDeposit * stageShare;
      }
      const previouslyInvoicedInclVat = (() => {
        if (selectedStageIndex <= 0) return 0;
        const prior = parts.slice(0, selectedStageIndex);
        return prior.reduce((acc, _, idx) => {
          if (idx === 0) return acc + depositGrossByPattern;
          const remainingParts = parts.slice(1);
          const sumRemaining = remainingParts.reduce((sum, value) => sum + Number(value || 0), 0);
          const priorPct = Number(parts[idx] || 0);
          const share = sumRemaining > 0 ? priorPct / sumRemaining : 0;
          return acc + (remainingAfterPatternDeposit * share);
        }, 0);
      })();
      const normalizedStageInclVat = r2(stageAmountInclVat);
      const stageExVat = effectiveVatEnabled && vatRate > 0
        ? r2(normalizedStageInclVat / (1 + vatRate))
        : normalizedStageInclVat;
      const stageVat = effectiveVatEnabled && vatRate > 0
        ? r2(Math.max(normalizedStageInclVat - stageExVat, 0))
        : 0;
      const remainingAfterThis = r2(Math.max(contractTotal - previouslyInvoicedInclVat - normalizedStageInclVat, 0));
      return {
        label: selectedStageIndex === 0 ? 'Stage 1 (Deposit)' : `Stage ${selectedStageIndex + 1}`,
        pct: stagePct,
        amountInclVat: normalizedStageInclVat,
        amountExVat: stageExVat,
        amountVat: stageVat,
        previouslyInvoicedInclVat: r2(previouslyInvoicedInclVat),
        remainingAfterThisStageInclVat: remainingAfterThis
      };
    })();

    // Some templates render Subtotal/VAT/Total unconditionally. When exporting an invoice scenario
    // (deposit-only / outstanding-only), override the core totals variables so the output matches
    // the chosen scenario.
    const projectGrossTotal = Number(grandTotal || 0);
    const displayGrossTotal = (() => {
      if (viewDoc.kind !== 'Invoice') return Number(grandTotal || 0);
      if (stageOnly) return stageComputation.amountInclVat;
      return invoicePdfDisplayGross({
        invoiceRole: viewDoc.invoiceRole,
        scenario,
        grandTotal,
        depositAmount: viewDoc.depositAmount,
      });
    })();
    const displayNetTotal = effectiveVatEnabled && vatRate > 0
      ? r2(displayGrossTotal / (1 + vatRate))
      : r2(displayGrossTotal);
    const displayVatAmount = effectiveVatEnabled && vatRate > 0
      ? r2(Math.max(displayGrossTotal - displayNetTotal, 0))
      : 0;

    const blobToDataUrl = async (blob: Blob): Promise<string> => {
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Failed to convert image blob to data URL'));
        reader.readAsDataURL(blob);
      });
    };

    const visualDesignerItems = await Promise.all(visualDesignerLines.map(async (line: any) => {
      const meta = (line as any).metaJson || {};
      const style = meta?.window_style || {};
      const specs = meta?.specs || {};
      const options = specs?.options || {};

      const toImg = (p0: any) => {
        const p = String(p0 || '').trim();
        if (!p) return '';
        if (p.startsWith('blob:')) return p;
        if (p.startsWith('http://') || p.startsWith('https://')) return p;
        return p.startsWith('/') ? p : `/${p}`;
      };

      const isUploadStyle = String(style?.source || '').trim().toLowerCase() === 'upload';
      let publicImgExternal = toImg(isUploadStyle ? style?.preview_path_external : (style?.preview_path_external || style?.preview_path));
      let publicImgInternal = toImg(style?.preview_path_internal);
      let imageUrlPresentationExternal = toImg(style?.preview_path_presentation_external);
      let imageUrlPresentationInternal = toImg(style?.preview_path_presentation_internal);
      let imageUrlDesignExternal = toImg(style?.preview_path_design_external);
      let imageUrlDesignInternal = toImg(style?.preview_path_design_internal);
      let imageUrlBaySchematic = toImg(style?.preview_path_bay_schematic || style?.preview_path_plan_external);

      // Render previews from one shared WG payload+renderer path so all exports stay aligned.
      const mergedPayload = buildLatestWindowGeneratorPayloadFromMeta(meta);
      if (
        !opts?.skipLiveV2PreviewRender &&
        String(style?.source || '').trim().toLowerCase() !== 'upload' &&
        mergedPayload &&
        mergedPayload.root &&
        mergedPayload.w != null &&
        mergedPayload.h != null
      ) {
        try {
          const rendered = await renderWindowGeneratorImageBlobs(mergedPayload);
          imageUrlPresentationExternal = await blobToDataUrl(rendered.presentationExternal);
          imageUrlPresentationInternal = await blobToDataUrl(rendered.presentationInternal);
          imageUrlDesignExternal = await blobToDataUrl(rendered.designExternal);
          imageUrlDesignInternal = await blobToDataUrl(rendered.designInternal);
          if (rendered.baySchematic) {
            imageUrlBaySchematic = await blobToDataUrl(rendered.baySchematic);
          }
          publicImgExternal = imageUrlPresentationExternal;
          publicImgInternal = imageUrlPresentationInternal;
        } catch {
          // Non-fatal: fall back to stored URLs
        }
      }

      const title = (specs?.reference || '').trim() || (style?.name || '').trim() || String(line.name || '');
      const typeName = (meta?.window_type?.name || '').trim();
      const materialName = (meta?.window_material?.name || '').trim();
      const summaryRows = getDesignerSummaryRows(meta as any).filter((r) => !!String(r?.value || '').trim());
      const variantLabelByCode: Record<string, string> = {
        chamfered: 'Chamfered (Bevelled)',
        sculptured: 'Sculptured (Ovolo)',
        'flush-sash': 'Flush Sash',
        'aluminium-contemporary': 'Contemporary',
        'aluminium-flush': 'Flush',
        'aluminium-flush-contemporary': 'Flush Contemporary',
        'aluminium-stepped-crittal': 'Stepped (Crittal)',
        'aluminium-flush-stepped': 'Flush Stepped',
        'timber-flush': 'Flush',
        'timber-timberweld-tm': 'Timberweld (TM)',
        'timber-casement': 'Casement',
        'aluminium-add': 'Add',
      };
      const variantText = (() => {
        const raw = String((specs as any)?.variant || '').trim();
        if (raw) return raw;
        const code = String((specs as any)?.variant_code || '').trim().toLowerCase();
        return variantLabelByCode[code] || '';
      })();
      const glassTypeText = (() => {
        const raw = String((specs as any)?.glass_type || '').trim();
        if (raw) return raw;
        const code = String((specs as any)?.glass_type_code || '').trim().toLowerCase();
        if (code === 'obscured') return 'Obscured';
        if (code === 'clear') return 'Clear';
        return '';
      })();
      const beadingTypeText = (() => {
        const raw = String((specs as any)?.beading_type || '').trim();
        if (raw) return raw;
        const code = String((specs as any)?.beading_type_code || '').trim().toLowerCase();
        if (code === 'chamfered') return 'Chamfered (Bevelled)';
        if (code === 'sculptured') return 'Sculptured (Ovolo)';
        if (code === 'flush-sash') return 'Flush Sash';
        const variantCode = String((specs as any)?.variant_code || '').trim().toLowerCase();
        if (variantLabelByCode[variantCode]) return variantLabelByCode[variantCode];
        return '';
      })();
      const extendersText = formatExtendersMmFromOptions(options as any);
      const georgianText = (() => {
        if ((options as any)?.georgian_bars !== true) return '';
        const cols = Number((options as any)?.georgian_cols || 0) || 0;
        const rowsN = Number((options as any)?.georgian_rows || 0) || 0;
        const wmm = Number((options as any)?.georgian_width_mm || 0) || 0;
        const grid = cols && rowsN ? `${cols}x${rowsN}` : cols ? `${cols} cols` : rowsN ? `${rowsN} rows` : '';
        return [grid, wmm ? `${wmm}mm` : ''].filter(Boolean).join(' ');
      })();
      const astragalText = (() => {
        if ((options as any)?.astragal_bars !== true) return '';
        const cols = Number((options as any)?.astragal_cols || 0) || 0;
        const rowsN = Number((options as any)?.astragal_rows || 0) || 0;
        const wmm = Number((options as any)?.astragal_width_mm || 0) || 0;
        const grid = cols && rowsN ? `${cols}x${rowsN}` : cols ? `${cols} cols` : rowsN ? `${rowsN} rows` : '';
        return [grid, wmm ? `${wmm}mm` : ''].filter(Boolean).join(' ');
      })();
      const isDoor = isDoorType(meta?.window_type);
      const hingeTypeText = (() => {
        if (isDoor) return '';
        const label = String((options as any)?.window_hinge_type || (options as any)?.hinge_type || '').trim();
        if (label) return label.replace(/\s*\(default\)\s*$/i, '');
        const code = String((options as any)?.window_hinge_type_code || (options as any)?.hinge_type_code || 'standard').trim().toLowerCase();
        if (code === 'egress') return 'Egress';
        if (code === 'ez_clean') return 'EZ Clean';
        if (code === 'fire_rated') return 'Fire-Rated';
        return 'Standard';
      })();
      const cillOpt =
        options?.cill_option === '150' || options?.cill_option === '180' || options?.cill_option === 'stub' || options?.cill_option === 'none'
          ? options?.cill_option
          : null;
      const cillPlacement = (() => {
        const payload = (meta as any)?.window_style?.templatePayload || (meta as any)?.window_style?.template_payload || null;
        const isUnionShape = !!(payload?.root && payload.root.type === 'rectUnion' && Array.isArray(payload.root.regions) && payload.root.regions.length > 1);
        if (!isUnionShape) return '';
        const explicitLabel = String((options as any)?.cill_union_mode_label || '').trim();
        if (explicitLabel) return explicitLabel;
        const pres = payload?.presentation || {};
        const rawMode = String((options as any)?.cill_union_mode || pres?.cillUnionMode || pres?.cill_union_mode || '').trim().toLowerCase();
        const includeUpper = (options as any)?.cill_union_include_upper === true || pres?.cillUnionIncludeUpper === true;
        const normalized =
          rawMode === 'window_only' || rawMode === 'window-only' || rawMode === 'window only' ? 'window_only'
          : rawMode === 'both' || rawMode === 'door_window' || rawMode === 'door+window' ? 'both'
          : rawMode === 'door_only' || rawMode === 'door-only' || rawMode === 'door only' ? 'door_only'
          : (includeUpper ? 'both' : 'door_only');
        if (normalized === 'window_only') return 'Window Only';
        if (normalized === 'both') return 'Door & Window';
        return 'Door Only';
      })();
      const derivedCill =
        cillOpt === '150' ? `150mm${cillPlacement ? ` (${cillPlacement})` : ''}`
        : cillOpt === '180' ? `180mm${cillPlacement ? ` (${cillPlacement})` : ''}`
        : cillOpt === 'stub' ? `Stub${cillPlacement ? ` (${cillPlacement})` : ''}`
        : cillOpt === 'none' ? 'None'
        : (options?.cill_type === 'Stub' ? 'Stub' : (options?.cill_height_mm ? `${options?.cill_height_mm}mm` : (options?.cill_included ? 'Included' : '')));

      return {
        // Primary title shown in PDF block: Reference first, else Style name
        name: title,
        // Secondary fields
        typeName,
        materialName,
        styleName: style?.name || '',
        styleId: style?.id ?? '',
        category: style?.category ?? '',
        reference: specs?.reference ?? '',
        widthMm: specs?.width_mm ?? '',
        heightMm: specs?.height_mm ?? '',
        glassType: glassTypeText,
        variant: variantText,
        beadingType: beadingTypeText,
        colour: specs?.frame_color ?? '',
        handleColour: (options?.handle_color || '').trim(),
        handleStyle: (options?.handle_style || '').trim(),
        hingeType: hingeTypeText,
        extenders: extendersText,
        georgian: georgianText,
        astragal: astragalText,
        astragalBarSide: String((options as any)?.astragal_bar_side_label || (options as any)?.astragal_bar_side || '').trim(),
        hingePlacement: (() => {
          const hp = String(options?.hinge_placement || '').trim().toLowerCase();
          if (hp === 'left') return 'Left';
          if (hp === 'right') return 'Right';
          return '';
        })(),
        openDirection: (() => {
          const od = String(options?.open_direction || '').trim().toLowerCase();
          if (od === 'in') return 'In';
          if (od === 'out') return 'Out';
          return '';
        })(),
        trickleVents: (() => {
          if (options?.trickle_vents === true) {
            const pos = formatTrickleVentsPositionLabel(options?.trickle_vents_position);
            return pos !== 'Yes' ? `Yes (${pos})` : 'Yes';
          }
          if (options?.trickle_vents === false) return 'No';
          return '';
        })(),
        cill: derivedCill,
        notes: specs?.notes ?? '',
        quantity: line.qty ?? 1,
        source: style?.source || '',
        // Back-compat: imageUrl remains the primary image (external)
        imageUrl: publicImgExternal,
        imageUrlExternal: publicImgExternal,
        imageUrlInternal: publicImgInternal,
        imageUrlPresentationExternal,
        imageUrlPresentationInternal,
        imageUrlDesignExternal,
        imageUrlDesignInternal,
        imageUrlBaySchematic,
        metaJson: (() => {
          try { return JSON.stringify(meta); } catch { return ''; }
        })(),
        summaryRows,
      };
    }));

    const templateData = {
      // Document info
      headerTitle: config?.headerTitle || viewDoc.kind.charAt(0).toUpperCase() + viewDoc.kind.slice(1),
      documentType: viewDoc.kind,
      documentTypeLower: viewDoc.kind.toLowerCase(),
      documentRef: documentDisplayRef(viewDoc),
      documentDate: invoiceDateForPdf
        ? formatInvoiceDateLong(invoiceDateForPdf)
        : (() => {
        const d = new Date(effectiveDate);
        const day = d.getDate();
        const suffix = (n: number) => {
          const j = n % 10, k = n % 100;
          if (j === 1 && k !== 11) return 'st';
          if (j === 2 && k !== 12) return 'nd';
          if (j === 3 && k !== 13) return 'rd';
          return 'th';
        };
        const month = d.toLocaleString('en-GB', { month: 'long' });
        const year = d.getFullYear();
        return `${day}${suffix(day)} ${month} ${year}`;
      })(),
      dueDate: normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit'
        ? ''
        : (dueDateForPdf ? formatInvoiceDateLong(dueDateForPdf) : ''),
      paymentTermsLabel: normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit'
        ? ''
        : (invoiceSchedule?.label || ''),
      paymentTermsWording: normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit'
        ? ''
        : (invoiceSchedule?.wording || ''),
      documentTitle: viewDoc.title || '',
      worksSummary: (() => {
        if (normalizeInvoiceRole(viewDoc.invoiceRole) !== 'deposit') return viewDoc.title || '';
        return depositInvoiceLineName({
          quotedGross: resolveQuotedGrossForDeposit({
            depositAmount: viewDoc.depositAmount,
            depositPercentage: viewDoc.depositPercentage,
          }),
          depositPercentage: viewDoc.depositPercentage,
          fallback: viewDoc.title,
        });
      })(),
      depositNarrative: depositNarrative || '',
      isDepositInvoice: normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit',
      depositClearedFundsNote: normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit'
        ? DEPOSIT_CLEARED_FUNDS_NOTE
        : '',
      isBalanceInvoice: normalizeInvoiceRole(viewDoc.invoiceRole) === 'works',
      isVariationInvoice: normalizeInvoiceRole(viewDoc.invoiceRole) === 'variation',
      quotedGross: (() => {
        const quoted = resolveQuotedGrossForDeposit({
          depositAmount: templateDepositAmount,
          depositPercentage: templateDepositPercentage,
        });
        return quoted > 0
          ? quoted.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
          : '';
      })(),
      description: renderDescriptionHtml(depositNarrative || viewDoc.description),
      documentArticle: /^[AEIOUaeiou]/.test(viewDoc.kind) ? 'an' : 'a',
      
      // Company info
      companyName: config?.companyName || 'Keystone Glazing',
      companyAddress: config?.companyAddress || '',
      companyPhone: config?.companyPhone || '',
      companyEmail: config?.companyEmail || '',
      companyVatNumber: config?.companyVatNumber || '',
      // Banking info (optional)
      bankName: (config as any)?.bankName || '',
      bankAccountName: (config as any)?.bankAccountName || '',
      bankAccountNumber: (config as any)?.bankAccountNumber || '',
      bankSortCode: (config as any)?.bankSortCode || '',
      bankIban: (config as any)?.bankIban || '',
      bankSwift: (config as any)?.bankSwift || '',
      logoPath: config?.logoPath || '',
      
      // Customer info
      customerName: documentIdentity.usesBilling
        ? (documentIdentity.lastName || documentIdentity.fullName || derivedLastName)
        : derivedLastName,
      customerFullName: documentIdentity.fullName
        || ((viewDoc.customerFirstName && viewDoc.customerLastName)
          ? `${viewDoc.customerFirstName} ${viewDoc.customerLastName}`
          : (customerData?.name || 'Customer')),
      customerTitle: documentIdentity.usesBilling ? (documentIdentity.title || '') : derivedCustomerTitle,
      customerEmail: documentIdentity.email || '',
      customerPhone: documentIdentity.phone || '',
      customerAddress: (() => {
        if (documentIdentity.usesBilling) {
          const billed = String(documentIdentity.address || '').trim();
          return billed ? billed.replace(/\n/g, '<br />') : '';
        }
        // Prefer live directory address; add common fallbacks; then document-provided
        const cd: any = customerData || {};
        const primary = cd.address ?? cd.customer_address ?? cd.billing_address ?? (viewDoc as any)?.customerAddress ?? '';
        const asString = (v: any) => (typeof v === 'string' ? v : String(v || ''));
        let raw = asString(primary).trim();
        // If empty, attempt to assemble from parts commonly found in directory schemas
        if (!raw) {
          const parts = [
            cd.address_line1 ?? cd.line1 ?? cd.street ?? cd.street1,
            cd.address_line2 ?? cd.line2 ?? cd.street2,
            cd.town ?? cd.city ?? cd.locality,
            cd.county ?? cd.region ?? cd.state,
            cd.postcode ?? cd.postal_code ?? cd.zip,
          ]
            .map(asString)
            .map(s => s.replace(/\bnull\b/gi, '').replace(/\bundefined\b/gi, '').trim())
            .filter(Boolean);
          raw = parts.join('\n');
        }
        const cleaned = raw
          .replace(/\bnull\b/gi, '')
          .replace(/\bundefined\b/gi, '')
          .trim();
        return cleaned ? cleaned.replace(/\n/g, '<br />') : '';
      })(),
      
      // Line items
      hasLineItems: exportLineItems.length > 0,
      lineItems: exportLineItems.map((line: any) => ({
        name: normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit'
          ? depositInvoiceLineName({
              quotedGross: resolveQuotedGrossForDeposit({
                depositAmount: viewDoc.depositAmount,
                depositPercentage: viewDoc.depositPercentage,
              }),
              depositPercentage: viewDoc.depositPercentage,
              fallback: line.name,
            })
          : line.name,
        description: line.description || (normalizeInvoiceRole(viewDoc.invoiceRole) === 'deposit' ? depositNarrative : '') || '',
        quantity: line.qty,
        unit: line.unit,
        unitPrice: money(line.unitPrice),
        lineTotal: money(line.lineTotal || 0)
      })),

      // Visual Designer (Quote Mode) items (JSON-driven; template-friendly)
      hasVisualDesignerItems: visualDesignerItems.length > 0,
      visualDesignerItems,
      
      // Financial data
      baseCostTotal: money(totals.baseCost),
      showFees: true,
      adminPct: (viewDoc.fees.adminPct * 100).toFixed(0),
      adminFee: money(totals.adminFee),
      contingencyPct: (viewDoc.fees.contingencyPct * 100).toFixed(0),
      contingency: money(totals.contingency),
      profitPct: (viewDoc.fees.profitPct * 100).toFixed(0),
      profit: money(totals.profit),
      // Reflect DB flag exactly; template decides what to show
      vatEnabled: !!effectiveVatEnabled,
      noVat: !effectiveVatEnabled,
      vatPct: (viewDoc.vatPct * 100).toFixed(0),
      vatAmount: money(displayVatAmount),
      // For deposit-only exports, keep {{grandTotal}} as the full project total
      // while dedicated deposit fields represent the deposit-only values.
      grandTotal: money(depositOnly ? projectGrossTotal : displayGrossTotal),
      totalExVat: money(displayNetTotal),
      totalIncVat: money(displayGrossTotal),
      materialsInclVat: money(goodsIncVat),
      materialsExcVat: money(materialsExcVat),
      labourInclVat: money(labourIncVat),
      labourExcVat: money(labourExcVat),
      labourDays: viewDoc.projectDays || '',
      labourHours: viewDoc.projectHours || '',
      labourDaysText: viewDoc.projectDays ? `${viewDoc.projectDays} day${viewDoc.projectDays === 1 ? '' : 's'}` : '',
      labourHoursText: viewDoc.projectHours ? `${viewDoc.projectHours} hour${viewDoc.projectHours === 1 ? '' : 's'}` : '',
      // For deposit-only exports, suppress the other VAT/deposit blocks entirely.
      hasDeposit: hasDepositForBlocks,
      noDeposit: noDepositForBlocks,
      depositDisabled: noDepositForBlocks,
      amountOutstanding: money(outstanding),
      depositAmount: money(templateDepositAmount),
      depositPct: templateDepositPercentage != null ? (Number(templateDepositPercentage) * 100).toFixed(0) : '',
      depositPercentage: templateDepositPercentage != null ? (Number(templateDepositPercentage) * 100).toFixed(0) : '',
      depositInvoiceReference: !!priorDepositNarrative,
      depositAlreadyInvoicedNarrative: priorDepositNarrative,
      depositInvoiceAmount: siblingDepositAmount > 0 ? money(siblingDepositAmount) : '',
      depositInvoiceRef: resolvedPriorDeposit?.invoiceRef || '',
      depositInvoicePct: siblingDepositPercentage != null ? (Number(siblingDepositPercentage) * 100).toFixed(0) : '',
      depositOnly,
      outstandingOnly,
      stageOnly,
      depositExVat: money(depositExVatValue),
      depositVatAmount: money(depositVatValue),
      paymentStructurePattern: activePaymentStructure?.label || (viewDoc as any).paymentStructurePattern || '',
      contractTotalInclVat: money(projectGrossTotal),
      stageLabel: stageComputation.label,
      stagePct: stageComputation.pct ? String(stageComputation.pct) : '',
      stageAmount: money(stageComputation.amountInclVat),
      stageExVat: money(stageComputation.amountExVat),
      stageVatAmount: money(stageComputation.amountVat),
      previouslyInvoicedInclVat: money(stageComputation.previouslyInvoicedInclVat),
      remainingAfterThisStageInclVat: money(stageComputation.remainingAfterThisStageInclVat),
    };

    // Expose for debugging in the browser console
    try { (window as any).lastTemplateData = templateData; } catch {}

    const renderedTemplate = renderDocumentTemplate({
      templateHtml,
      templateData: templateData as Record<string, any>,
      cleanupMode: 'default',
    });

    return renderedTemplate;
  };

  const handleDateConfirm = async (
    selectedDate: string,
    scenario?: InvoicePdfScenario | null,
    stageIndex?: number | null
  ) => {
    try {
      // Quotes/estimates remember issuedAt. Unlocked invoices persist the chosen issue date
      // so PDF, JARVIS, and FreeAgent stay on one schedule. Workspace documents only export here.
      if (!workspaceReadOnly && viewDoc.kind !== 'Invoice') {
        if (selectedDate !== viewDoc.issuedAt) {
          await documentsService.updateDocument(viewDoc.id, { issuedAt: selectedDate });
          setDoc({ ...doc, issuedAt: selectedDate });
        }
      } else if (!workspaceReadOnly && !invoiceDatesLocked && toDateOnly(selectedDate) !== invoiceSchedule?.invoiceDate) {
        const nextIssuedAt = dateOnlyToIssuedAt(toDateOnly(selectedDate) || invoiceSchedule?.invoiceDate || '');
        const updated = await documentsService.updateDocument(viewDoc.id, { issuedAt: nextIssuedAt });
        setDoc((prev) => (prev ? { ...prev, ...updated } : prev));
      }
      if (viewDoc.kind === 'Invoice' && scenario) {
        setInvoicePdfScenario(scenario);
        setInvoicePdfStageIndex(scenario === 'stage_only' ? (stageIndex ?? 0) : null);
      }
      // Export PDF with the selected date
      await exportPdf(
        selectedDate,
        viewDoc.kind === 'Invoice' ? (scenario || invoicePdfScenario) : null,
        viewDoc.kind === 'Invoice'
          ? ((scenario === 'stage_only') ? (stageIndex ?? 0) : (invoicePdfStageIndex ?? null))
          : null
      );
    } catch (error) {
      console.error('Error updating document date or exporting PDF:', error);
      alert('Failed to update document date or export PDF. Please try again.');
    }
  };

  const buildDocumentExportHtml = (renderedTemplate: string): string => {
    const docType = viewDoc.kind.charAt(0).toUpperCase() + viewDoc.kind.slice(1);
    const logoPath = config?.logoPath || '';
    const exportLogoHtml = (() => {
      const p = String(logoPath || '').trim();
      if (!p) return '';
      // Avoid duplicating if the template already references this logo path.
      if (renderedTemplate.includes(p)) return '';
      return `<div class="export-logo"><img src="${p}" alt="Company Logo" /></div>`;
    })();
    return `<!doctype html><html><head><meta charset="utf-8"><title>${docType}</title>
      <base href="${window.location.origin}/" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>
        ${buildSharedDocumentCss({
          fontFamily: config?.fontFamily || 'Poppins, Arial, sans-serif',
          fontSizeBase: config?.fontSizeBase || 14,
          headerColor: config?.headerColor || '#1976d2',
          primaryColor: config?.primaryColor || '#1976d2',
          secondaryColor: config?.secondaryColor || '#666666',
          logoWidth: config?.logoWidth || 200,
          logoHeight: config?.logoHeight || 100,
          layoutPreset: STANDARD_DOC_LAYOUT,
          useFlexLayout: true,
          includeExportLogo: true,
        })}
      </style>
    </head><body>
      <main id="main-content">
        ${paidWatermarkMarkup(viewDoc.kind, viewDoc.status)}
        <!-- Our document pages (with header/footer overlay images) -->
        <div class="our-doc">
          ${config?.headerImagePath ? `<div class="header-image"><img src="${config.headerImagePath}" alt="Header Image" style="max-width:100%;height:auto;max-height:150px;object-fit:contain;" /></div>` : ''}
          ${exportLogoHtml}
          <div class="content-wrapper">${renderedTemplate}</div>
          ${config?.footerImagePath ? `<footer id="doc-footer"><img src="${config.footerImagePath}" alt="Footer Image" /></footer>` : ''}
        </div>

      </main>
    </body></html>`;
  };

  const exportPdf = async (
    documentDate?: string,
    scenario?: InvoicePdfScenario | null,
    stageIndex?: number | null
  ) => {
    const printable = document.getElementById('document-print-area');
    if (!printable) return;
    
    // Use the config that was already loaded in useEffect
    if (!config) {
      console.warn('No PDF configuration loaded, using defaults');
    }
    
    const docType = viewDoc.kind.charAt(0).toUpperCase() + viewDoc.kind.slice(1);
    const exportBase = `${docType} - ${documentDisplayRef(viewDoc)} - ${viewDoc.title}`;
    
    console.log('Using PDF config:', config);
    
    // Render the custom template with real data
    console.log('Using template:', template);
    console.log('Template length:', template?.length || 0);
    const vdObjectUrls: string[] = [];
    const renderExportTemplate = (skipLiveV2PreviewRender: boolean) =>
      renderTemplate(template, documentDate, scenario || null, stageIndex ?? null, {
        vdObjectUrls,
        skipLiveV2PreviewRender,
      });
    let renderedTemplate = await renderExportTemplate(false);
    console.log('Rendered template length:', renderedTemplate?.length || 0);
    
    // Fallback to default content if no template
    if (!renderedTemplate || renderedTemplate.trim() === '') {
      console.warn('No template available or template is empty, using fallback content');
      renderedTemplate = printable.innerHTML;
    }
    
    console.log('Rendered template:', renderedTemplate);
    
    setIsExportingPdf(true);

    const buildExportHtml = (): string => buildDocumentExportHtml(renderedTemplate);
    const toAbsoluteAssetUrl = (pathOrUrl?: string | null): string => {
      const raw = String(pathOrUrl || '').trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
      try { return new URL(raw, window.location.origin).toString(); } catch { return raw; }
    };

    const buildPdfHeaderFooterOptions = (opts: { headerImagePath?: string | null; footerImagePath?: string | null; footerHeightPx?: number }) => {
      const headerSrc = toAbsoluteAssetUrl(opts.headerImagePath || '');
      const footerSrc = toAbsoluteAssetUrl(opts.footerImagePath || '');
      const sideMarginPx = 36;
      const headerHeightPx = 130;
      const footerHeightPx = opts.footerHeightPx ?? 66;
      const headerGapPx = 0;
      const footerGapPx = 14;
      const topMarginPx = headerSrc ? (headerHeightPx + headerGapPx) : sideMarginPx;
      const bottomMarginPx = footerSrc ? (footerHeightPx + footerGapPx) : sideMarginPx;
      const headerTemplate = headerSrc
        ? `<div style="width:100%;height:${headerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${headerSrc}" alt="PDF Header" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
        : '';
      const footerTemplate = footerSrc
        ? `<div style="width:100%;height:${footerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${footerSrc}" alt="PDF Footer" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
        : '';
      return {
        displayHeaderFooter: !!(headerTemplate || footerTemplate),
        headerTemplate,
        footerTemplate,
        margin: {
          top: `${topMarginPx}px`,
          right: `${sideMarginPx}px`,
          bottom: `${bottomMarginPx}px`,
          left: `${sideMarginPx}px`,
        },
      };
    };

    const extractPdfAssetsFromHtml = (rawHtml: string): { cleanedHtml: string; headerImagePath: string; footerImagePath: string } => {
      const srcRe = /src="([^"]+)"/i;
      const headerBlockRe = /<div[^>]*class="[^"]*header-image[^"]*"[^>]*>[\s\S]*?<\/div>/i;
      const footerBlockRe = /<footer[^>]*id="doc-footer"[^>]*>[\s\S]*?<\/footer>/i;
      const headerMatch = rawHtml.match(headerBlockRe);
      const footerMatch = rawHtml.match(footerBlockRe);
      const headerSrc = headerMatch?.[0]?.match(srcRe)?.[1] || '';
      const footerSrc = footerMatch?.[0]?.match(srcRe)?.[1] || '';
      const cleanedHtml = withPdfPrintHeightReset(
        stripTrailingEmptyPdfPagesHtml(
          rawHtml
            .replace(headerBlockRe, '')
            .replace(footerBlockRe, '')
            .replace(/@page\s*\{[^}]*\}/gi, '@page { size: A4; }'),
        ),
      );
      return { cleanedHtml, headerImagePath: headerSrc, footerImagePath: footerSrc };
    };

    try {
      const requestPdfExport = async (): Promise<Blob> => {
        const html = buildExportHtml();
        const extracted = extractPdfAssetsFromHtml(html.replace('<title>Document</title>', `<title>${exportBase}</title>`));
        const pdfOptions = buildPdfHeaderFooterOptions({
          headerImagePath: extracted.headerImagePath || config?.headerImagePath || '',
          footerImagePath: extracted.footerImagePath || config?.footerImagePath || '',
          footerHeightPx: await pdfFooterHeightPx(extracted.footerImagePath || config?.footerImagePath || ''),
        });
        const response = await fetch('/api/export-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: extracted.cleanedHtml, pdfOptions }),
        });
        if (!response.ok) {
          const message = await response.text().catch(() => '');
          const error = new Error(message || `PDF export failed (${response.status})`);
          (error as any).status = response.status;
          throw error;
        }
        return response.blob();
      };

      const isOversizedPdfPayloadError = (error: any): boolean => {
        const status = Number(error?.status || 0);
        const message = String(error?.message || '');
        return status === 413 || /payload too large|request entity too large|function_payload_too_large/i.test(message);
      };
      const isPdfRenderTimeoutError = (error: any): boolean => {
        const message = String(error?.message || '');
        return /navigation timeout|timeout of \d+\s*ms exceeded|timed out/i.test(message);
      };

      let pdfBlob: Blob | null = null;
      try {
        pdfBlob = await requestPdfExport();
      } catch (error: any) {
        if (!isOversizedPdfPayloadError(error) && !isPdfRenderTimeoutError(error)) {
          throw error;
        }
        console.warn('PDF export retrying with stored Visual Designer preview URLs after render failure.');
        const fallbackTemplate = await renderExportTemplate(true);
        if (!fallbackTemplate || fallbackTemplate.trim() === '') throw error;
        renderedTemplate = fallbackTemplate;
        pdfBlob = await requestPdfExport();
      }

      if (!pdfBlob) return;

      const pdfUrl = URL.createObjectURL(pdfBlob);
      const anchor = document.createElement('a');
      anchor.href = pdfUrl;
      anchor.download = `${exportBase.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim()}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(pdfUrl);
    } finally {
      setIsExportingPdf(false);
      try { vdObjectUrls.forEach(u => URL.revokeObjectURL(u)); } catch {}
    }
  };

  const applyDefaultCustomerSend = async (email: string) => {
    try {
      setIsSendingToCustomer(true);
      const ready = await ensureSignedIn();
      if (!ready) {
        setNoticeDialog({ title: 'Sign in required', body: GRAPH_SIGNIN_REQUIRED_TO_SEND });
        return;
      }

      const sendResult = await sendDocumentToCustomer(doc.id, {
        versionId: String(activeVersion?.header?.id || doc.activeVersionId || '').trim() || null,
        surveyArtefact: 'first',
      });
      setDoc(prev => (prev ? { ...prev, status: 'sent' as any } : prev));
      await refreshShareLinks(doc.id);
      setNoticeDialog({
        title: 'Sent successfully',
        body: `Sent this ${doc.kind.toLowerCase()} to ${email}.`,
        url: sendResult.shareUrl,
      });
    } catch (e: any) {
      console.error('Failed to send document to customer:', e);
      setNoticeDialog({
        title: 'Failed to send',
        body: e?.message || 'Unknown error',
      });
    } finally {
      setIsSendingToCustomer(false);
    }
  };

  const openPublicDocPreview = async () => {
    if (!viewDoc) return;
    const previewId = createOfflinePublicDocPreviewId();
    try {
      markOfflinePublicDocPreviewPending(previewId);
    } catch {
      // The workspace modal still opens if local storage is unavailable.
    }
    const previewTab = window.open(offlinePublicDocPreviewHref(previewId), '_blank');
    try {
      if (previewTab) previewTab.opener = null;
    } catch {
      // The new tab can still show the preview if this browser hides the opener.
    }
    setPublicPreviewLoading(true);
    setPublicPreviewError(null);
    setPublicPreviewTabNote(null);
    try {
      let visualDesignerLines: Array<{ name: string | null; qty: number | null; metaJson: any }> = [];
      let versionNumber: number | null = null;
      if (viewDoc.kind === 'Invoice') {
        visualDesignerLines = ((viewDoc as any).lines || [])
          .filter((line: any) => isVisualDesignerLine(line))
          .map((line: any) => ({
            name: line.name ?? null,
            qty: line.qty ?? null,
            metaJson: line.metaJson ?? null,
          }));
      } else {
        const versionId = String(activeVersion?.header?.id || (viewDoc as any).activeVersionId || '').trim();
        if (versionId) {
          const version = await documentsService.getVersion(versionId);
          const rawNumber = Number(version.header?.version_number);
          versionNumber = Number.isFinite(rawNumber) ? Math.trunc(rawNumber) : null;
          visualDesignerLines = (version.lines || [])
            .map((line: any) => ({
              name: line.name ?? null,
              qty: line.qty ?? null,
              metaJson: line.meta_json ?? null,
            }))
            .filter((line) => isVisualDesignerLine(line));
        }
        if (visualDesignerLines.length === 0 && pairedSurvey?.id) {
          const items = await visualDesignerSurveysService.listItems(pairedSurvey.id);
          visualDesignerLines = items
            .filter((line) => isVisualDesignerLine(line))
            .map((line) => ({
              name: line.name ?? null,
              qty: line.qty ?? null,
              metaJson: line.metaJson ?? null,
            }));
        }
      }

      let pdfHtml: string | null = null;
      if (template) {
        try {
          const renderedTemplate = await renderTemplate(template, undefined, undefined, undefined, { skipLiveV2PreviewRender: true });
          pdfHtml = renderedTemplate ? buildDocumentExportHtml(renderedTemplate) : null;
        } catch {
          pdfHtml = null;
        }
      }

      let finalSurveyReapproval: Record<string, unknown> | null = null;
      const finalSurveyId = String(offlinePreviewFinalSurveyId || (pairedSurvey?.kind === 'final' ? pairedSurvey.id : '')).trim();
      if (finalSurveyId) {
        const { data: deltaRow, error: deltaError } = await directSupabase
          .from('final_survey_deltas')
          .select('id, final_survey_id, classification, snapshot_json, customer_accepted_at, superseded_at')
          .eq('final_survey_id', finalSurveyId)
          .is('superseded_at', null)
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!deltaError) finalSurveyReapproval = finalReapprovalFromSavedDelta(deltaRow);
      }

      const names = String(documentIdentity.fullName || '').trim().split(/\s+/).filter(Boolean);
      const payload = buildOfflinePublicDocPreview({
        kind: viewDoc.kind,
        status: viewDoc.status,
        acceptedAt: (viewDoc as any).acceptedAt ?? null,
        lifecycleRef: viewDoc.lifecycleRef,
        invoiceRef: (viewDoc as any).invoiceRef ?? null,
        title: viewDoc.title,
        description: viewDoc.description,
        currency: viewDoc.currency,
        vatEnabled: viewDoc.vatEnabled,
        netTotal: viewDoc.netTotal ?? null,
        vatAmount: viewDoc.vatAmount ?? null,
        grossTotal: viewDoc.grossTotal ?? null,
        issuedAt: viewDoc.issuedAt ?? null,
        invoiceRole: viewDoc.invoiceRole ?? null,
        paymentTermsInDays: viewDoc.paymentTermsInDays ?? null,
        projectDays: viewDoc.projectDays ?? null,
        projectHours: viewDoc.projectHours ?? null,
        hasDeposit: viewDoc.hasDeposit,
        depositAmount: viewDoc.depositAmount ?? null,
        depositPercentage: viewDoc.depositPercentage ?? null,
        versionNumber,
        organisationName: config?.companyName || null,
        customer: {
          title: documentIdentity.title,
          forename: documentIdentity.firstName || names[0] || null,
          surname: documentIdentity.lastName || names.slice(1).join(' ') || null,
          address: documentIdentity.address || null,
          email: documentIdentity.email || null,
          phone: documentIdentity.phone || null,
        },
        visualDesignerLines,
        pdfHtml,
        finalSurveyReapproval,
      });
      const stored = publishOfflinePublicDocPreview(previewId, payload);
      if (stored === 'too-large') {
        if (previewTab && !previewTab.closed) previewTab.close();
        setPublicPreviewTabNote('This preview is too large to open in a new tab.');
      }
    } catch (previewError: any) {
      if (previewTab && !previewTab.closed) previewTab.close();
      setPublicPreviewError(previewError?.message || 'Could not build the public document preview.');
    } finally {
      setPublicPreviewLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: embedded ? '100%' : { xs: '100%', sm: '100%', md: '90%', lg: '66%' }, 
      mx: embedded ? 0 : 'auto',
      px: embedded ? 0 : { xs: 1, sm: 2 }
    }}>
      {doc && !embedded && !workspaceBillingDetailsAreValid({ project: workspaceProject, customer: customerData }) && (
        <Alert color="danger" sx={{ mb: 2 }}>
          <Typography level="title-sm">{BILLING_DETAILS_REQUIRED_TITLE}</Typography>
          <Typography level="body-sm">{BILLING_DETAILS_REQUIRED_MESSAGE}</Typography>
        </Alert>
      )}
      {workspaceReadOnly && (
        <Alert
          color="primary"
          variant="soft"
          sx={{ mb: 2 }}
          endDecorator={workspacePath ? (
            <Button size="sm" variant="solid" onClick={() => navigate(workspacePath)}>
              Open workspace
            </Button>
          ) : undefined}
        >
          {WORKSPACE_DOCUMENT_READ_ONLY_MESSAGE}
        </Alert>
      )}
      {(publicPreviewTabNote || publicPreviewError) && (
        <Alert color={publicPreviewError ? 'danger' : 'warning'} sx={{ mb: 2 }}>
          {publicPreviewError || publicPreviewTabNote}
        </Alert>
      )}
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        // Keep header stacked up to desktop widths so long titles don't get squeezed vertically
        flexDirection: { xs: 'column', xl: 'row' },
        flexWrap: { xs: 'nowrap', xl: 'wrap' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', xl: 'flex-start' },
        rowGap: 1.5,
        mb: 3,
        gap: 2
      }}>
        <Box sx={{ flex: { xs: '1 1 auto', xl: '1 1 420px' }, minWidth: { xs: 0, xl: 320 } }}>
          <Typography
            level="h3"
            sx={{ mb: 1, wordBreak: 'normal', overflowWrap: 'anywhere' }}
          >
            {(viewDoc as any)?.title || doc.title}
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ flexWrap: 'wrap' }}
          >
            <Chip 
              size="sm" 
              variant="soft" 
              color={doc.kind === 'Estimate' ? 'primary' : doc.kind === 'Quotation' ? 'success' : 'warning'}
            >
              {doc.kind}
            </Chip>
            {doc.kind === 'Invoice' && (
              <Chip size="sm" variant="soft" color="primary">{invoiceRoleLabel(doc.invoiceRole)}</Chip>
            )}
            <Chip size="sm" variant="outlined">{documentDisplayRef(viewDoc || doc)}</Chip>
            {!embedded && (
              <Chip size="sm" variant="soft" color={
                doc.kind === 'Invoice'
                  ? invoiceDisplayStatus({
                    status: doc.status,
                    customerTransmitted: shareLinks.length > 0,
                  }).color
                  : 'neutral'
              }>
                {doc.kind === 'Invoice'
                  ? invoiceDisplayStatus({
                    status: doc.status,
                    customerTransmitted: shareLinks.length > 0,
                  }).label
                  : doc.status}
              </Chip>
            )}
          </Stack>
        </Box>
        <Stack 
          direction={{ xs: 'column', xl: 'row' }} 
          spacing={1}
          sx={{ 
            width: { xs: '100%', xl: 'auto' },
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', xl: 'flex-end' },
            flexShrink: 0,
            maxWidth: '100%',
            '& > *': { 
              width: { xs: '100%', xl: 'auto' },
              minWidth: { xs: 'auto', xl: '120px' }
            }
          }}
        >
          {!hideCustomerSend && !workspaceReadOnly && (doc.kind === 'Estimate' || doc.kind === 'Quotation' || doc.kind === 'Invoice') && (
            <Button
              size="sm"
              variant="solid"
              startDecorator={<IconWrapper icon={FaPaperPlane} />}
              loading={isSendingToCustomer || customerSendBusy}
              disabled={isSendingToCustomer || customerSendBusy || !workspaceBillingDetailsAreValid({ project: workspaceProject, customer: customerData })}
              onClick={() => {
                if (isSendingToCustomer || customerSendBusy) return;
                if (!workspaceBillingDetailsAreValid({ project: workspaceProject, customer: customerData })) {
                  setNoticeDialog({
                    title: BILLING_DETAILS_REQUIRED_TITLE,
                    body: BILLING_DETAILS_REQUIRED_MESSAGE,
                  });
                  return;
                }
                if (onCustomerSend) {
                  onCustomerSend();
                  return;
                }
                if (!doc.customerId) {
                  setNoticeDialog({ title: 'Cannot send', body: 'No customer linked to this document.' });
                  return;
                }
                if (!documentIdentity.email) {
                  setNoticeDialog({
                    title: 'Cannot send',
                    body: documentIdentity.usesBilling
                      ? BILLING_DETAILS_REQUIRED_MESSAGE
                      : 'Customer email not found. Please add an email address to the customer record.',
                  });
                  return;
                }
                const email = String(documentIdentity.email);
                void (async () => {
                  setIsSendingToCustomer(true);
                  try {
                    const ready = await ensureSignedIn();
                    if (!ready) {
                      setNoticeDialog({ title: 'Sign in required', body: GRAPH_SIGNIN_REQUIRED_TO_SEND });
                      return;
                    }
                  } catch (e: any) {
                    setNoticeDialog({
                      title: 'Sign in required',
                      body: e?.message || GRAPH_SIGNIN_REQUIRED_TO_SEND,
                    });
                    return;
                  } finally {
                    setIsSendingToCustomer(false);
                  }
                  setConfirmDialog({
                    title: `Send ${doc.kind}`,
                    body: `Send this ${doc.kind.toLowerCase()} to ${email}?`,
                    confirmLabel: 'Send',
                    onConfirm: () => { void applyDefaultCustomerSend(email); },
                  });
                })();
              }}
            >
              Send
            </Button>
          )}
          {!embedded && (
            <Button 
              size="sm" 
              variant="soft" 
              startDecorator={<IconWrapper icon={FaDownload} />} 
              onClick={handleExportPdf}
              loading={isExportingPdf}
              disabled={isExportingPdf}
            >
              Export PDF
            </Button>
          )}
          {!embedded && (
            <Button
              size="sm"
              variant="outlined"
              onClick={() => setSnapshotOpen(true)}
            >
              Export Snapshot
            </Button>
          )}
          {embedded && (doc.kind === 'Estimate' || doc.kind === 'Quotation' || doc.kind === 'Invoice') && (
            <Button
              size="sm"
              variant="outlined"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void openPublicDocPreview();
              }}
              loading={publicPreviewLoading}
            >
              Preview
            </Button>
          )}
          {onClose && (
            <Button size="sm" variant="plain" onClick={onClose}>
              Close
            </Button>
          )}
          {!workspaceReadOnly && (
          <Button
            size="sm"
            variant="outlined"
            disabled={!!editDisabledReason}
            title={editDisabledReason || undefined}
            onClick={() => {
              if (editDisabledReason) {
                window.alert(editDisabledReason);
                return;
              }
              if (onEdit) onEdit(doc.id);
              else navigate(`/docs/edit/${doc.id}`);
            }}
          >
            Edit
          </Button>
          )}
          {!workspaceReadOnly && doc.kind === 'Estimate' && canProgressUnsentEstimate({
            estimateStatus: doc.status,
            hasQuote: false,
            firstHasBeenTransmitted: !!surveyDelete?.disabledReason,
          }) && (
            <Button
              size="sm"
              onClick={async () => {
                try {
                  const created = await documentsService.cloneToQuotation(doc.id);
                  if (onDocumentChanged) onDocumentChanged(created.id);
                  else navigate(`/docs/${created.id}`);
                } catch (error: any) {
                  console.error('Error converting to quotation:', error);
                  alert(`Failed to convert: ${error.message}`);
                }
              }}
            >
              Progress to Quote
            </Button>
          )}
          {!embedded && !workspaceReadOnly && doc.kind === 'Quotation' && (
            <Button
              size="sm"
              onClick={() => {
                if (onRequestCreateInvoice) {
                  onRequestCreateInvoice(doc.id);
                  return;
                }
                void (async () => {
                  try {
                    const created = await documentsService.issueBalanceInvoice(doc.id, {
                      settledContract: Number(doc.grossTotal || 0),
                    });
                    if (!created) {
                      alert('Nothing to invoice — settled contract already matches invoiced totals, or Final has not settled.');
                      return;
                    }
                    if (onDocumentChanged) onDocumentChanged(created.id);
                    else navigate(`/docs/${created.id}`);
                  } catch (error: any) {
                    console.error('Error creating invoice:', error);
                    alert(`Failed to create invoice: ${error.message}`);
                  }
                })();
              }}
            >
              Create Invoice
            </Button>
          )}
          {allowDelete && !workspaceReadOnly && !surveyDelete?.disabledReason && (
          <Button 
            size="sm" 
            variant="outlined" 
            color="danger"
            onClick={() => {
              if (surveyDelete) {
                setConfirmDialog({
                  title: `Delete ${surveyDelete.surveyLabel}`,
                  body: surveySheetDeleteConfirmMessage(surveyDelete.surveyLabel),
                  confirmLabel: 'Delete',
                  confirmColor: 'danger',
                  onConfirm: () => {
                    void (async () => {
                      try {
                        await surveyDelete.onDelete();
                      } catch (error) {
                        console.error('Error clearing survey:', error);
                        setNoticeDialog({
                          title: 'Failed to delete',
                          body: error instanceof Error ? error.message : 'Failed to delete survey. Please try again.',
                        });
                      }
                    })();
                  },
                });
                return;
              }

              const confirmed = window.confirm(`Are you sure you want to delete this ${doc.kind}? This action cannot be undone.`);
              if (!confirmed) return;
              void (async () => {
                try {
                  await documentsService.deleteDocument(doc.id);
                  alert(`${doc.kind.charAt(0).toUpperCase() + doc.kind.slice(1)} deleted successfully.`);
                  if (embedded && onDeleted) {
                    onDeleted();
                  } else if (embedded && onClose) {
                    onClose();
                  } else {
                    navigate('/docs');
                  }
                } catch (error) {
                  console.error('Error deleting document:', error);
                  alert('Failed to delete document. Please try again.');
                }
              })();
            }}
          >
            Delete
          </Button>
          )}
        </Stack>
      </Box>

      {!embedded && (
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Project Workflow Summary
        </Typography>
        {String(doc.projectId || '').trim() === '' ? (
          <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              No project linked to this document.
            </Typography>
          </Sheet>
        ) : linkedProjectLoading ? (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Loading linked project workflow summary…
          </Typography>
        ) : linkedProjectError ? (
          <Alert color="danger" variant="soft">
            {linkedProjectError}
          </Alert>
        ) : !linkedProject || !linkedProjectWorkflowSummary ? (
          <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Linked project was not found.
            </Typography>
          </Sheet>
        ) : (
          <Stack spacing={1.25}>
            <Grid container spacing={1}>
              <Grid xs={12} md={6}>
                <Typography level="body-sm">
                  <strong>Lifecycle status:</strong> {formatLifecycleStatusLabel(linkedProject.project_status)}
                </Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm">
                  <strong>Stage:</strong>{' '}
                  {linkedSnapshot
                    ? [workspaceStageLabel(linkedSnapshot.stage), stateShortLabel(linkedSnapshot.state)].filter(Boolean).join(' · ')
                    : '…'}
                </Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>Next:</strong> {linkedSnapshot?.reason || 'Nothing waiting'}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>Survey date:</strong> {formatDateLabel(linkedProjectWorkflowSummary.surveyDate)}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>First survey date:</strong> {formatDateLabel(linkedProjectWorkflowSummary.firstSurveyDate)}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>Final survey date:</strong> {formatDateLabel(linkedProjectWorkflowSummary.finalSurveyDate)}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm">
                  <strong>Order date:</strong>{' '}
                  {linkedProjectWorkflowSummary.orderDate
                    ? formatDateLabel(linkedProjectWorkflowSummary.orderDate)
                    : linkedProjectWorkflowSummary.orderedFlag
                      ? 'Ordered (date not captured)'
                      : '—'}
                </Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>Install date:</strong> {formatDateLabel(linkedProjectWorkflowSummary.installDate)}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>Install/Delivery event date:</strong> {formatDateLabel(linkedProjectWorkflowSummary.installationOrDeliveryDate)}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm"><strong>Completion date:</strong> {formatDateLabel(linkedProjectWorkflowSummary.completionDate)}</Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-sm">
                  <strong>Email automation status:</strong>{' '}
                  {Object.keys(linkedProjectWorkflowSummary.emailStatusSummary).length === 0
                    ? 'No email events yet'
                    : Object.entries(linkedProjectWorkflowSummary.emailStatusSummary)
                        .map(([status, count]) => `${status}: ${count}`)
                        .join(' · ')}
                </Typography>
              </Grid>
              {linkedProjectWorkflowSummary.lastEmailEvent && (
                <Grid xs={12}>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Last email event: {String(linkedProjectWorkflowSummary.lastEmailEvent.event_type || 'Unknown')} ({String(linkedProjectWorkflowSummary.lastEmailEvent.email_status || 'unknown')})
                    {' · '}
                    {formatDateTimeLabel(
                      linkedProjectWorkflowSummary.lastEmailEvent.email_sent_at
                      || linkedProjectWorkflowSummary.lastEmailEvent.event_date
                      || linkedProjectWorkflowSummary.lastEmailEvent.created_at
                    )}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {workflowRiver && (
              <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md' }}>
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.75 }}>
                  Workflow stage flow
                </Typography>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start', width: '100%' }}>
                  {workflowRiver.milestones.map((milestone, idx) => {
                    const isReached = idx <= workflowRiver.activeIndex;
                    const connectorReached = idx < workflowRiver.activeIndex;
                    const dotColor = workflowRiver.isCancelled
                      ? (isReached ? 'danger.500' : 'neutral.300')
                      : (isReached ? 'primary.500' : 'neutral.300');
                    const dotBorderColor = workflowRiver.isCancelled
                      ? (isReached ? 'danger.500' : 'neutral.400')
                      : (isReached ? 'primary.500' : 'neutral.400');
                    const dotShadow = workflowRiver.isCancelled
                      ? (isReached ? '0 0 0 3px rgba(211, 47, 47, 0.18)' : 'none')
                      : (isReached ? '0 0 0 3px rgba(33, 150, 243, 0.18)' : 'none');
                    const connectorColor = workflowRiver.isCancelled
                      ? (connectorReached ? 'danger.500' : 'neutral.300')
                      : (connectorReached ? 'primary.500' : 'neutral.300');
                    const labelColor = workflowRiver.isCancelled
                      ? (isReached ? 'danger.700' : 'text.secondary')
                      : (isReached ? 'primary.700' : 'text.secondary');
                    return (
                      <Box
                        key={milestone}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: 20, position: 'relative' }}>
                          {idx < workflowRiver.milestones.length - 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '100%',
                                height: 3,
                                transform: 'translateY(-50%)',
                                borderRadius: 999,
                              bgcolor: connectorColor,
                                transition: 'all 0.2s ease',
                                zIndex: 0,
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: dotColor,
                              border: '2px solid',
                              borderColor: dotBorderColor,
                              boxShadow: dotShadow,
                              transition: 'all 0.2s ease',
                              flexShrink: 0,
                              position: 'relative',
                              zIndex: 1,
                            }}
                          />
                        </Box>
                        <Typography
                          level="body-xs"
                          sx={{
                            textAlign: 'center',
                            color: labelColor,
                            lineHeight: 1.2,
                            mt: 0.5,
                              px: 0.35,
                              minHeight: 30,
                              fontSize: '0.72rem',
                              maxWidth: '100%',
                              overflowWrap: 'anywhere',
                          }}
                        >
                          {milestone}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Stack spacing={0.75} sx={{ display: { xs: 'flex', md: 'none' } }}>
                  {workflowRiver.milestones.map((milestone, idx) => {
                    const isReached = idx <= workflowRiver.activeIndex;
                    const connectorReached = idx < workflowRiver.activeIndex;
                    const dotColor = workflowRiver.isCancelled
                      ? (isReached ? 'danger.500' : 'neutral.300')
                      : (isReached ? 'primary.500' : 'neutral.300');
                    const dotBorderColor = workflowRiver.isCancelled
                      ? (isReached ? 'danger.500' : 'neutral.400')
                      : (isReached ? 'primary.500' : 'neutral.400');
                    const dotShadow = workflowRiver.isCancelled
                      ? (isReached ? '0 0 0 2px rgba(211, 47, 47, 0.18)' : 'none')
                      : (isReached ? '0 0 0 2px rgba(33, 150, 243, 0.18)' : 'none');
                    const connectorColor = workflowRiver.isCancelled
                      ? (connectorReached ? 'danger.500' : 'neutral.300')
                      : (connectorReached ? 'primary.500' : 'neutral.300');
                    const labelColor = workflowRiver.isCancelled
                      ? (isReached ? 'danger.700' : 'text.secondary')
                      : (isReached ? 'primary.700' : 'text.secondary');
                    return (
                      <Box key={`mobile-${milestone}`} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ width: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: dotColor,
                              border: '2px solid',
                              borderColor: dotBorderColor,
                              boxShadow: dotShadow,
                              mt: 0.2,
                            }}
                          />
                          {idx < workflowRiver.milestones.length - 1 && (
                            <Box
                              sx={{
                                mt: 0.25,
                                width: 2,
                                flex: 1,
                                minHeight: 14,
                                borderRadius: 999,
                                bgcolor: connectorColor,
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          level="body-xs"
                          sx={{
                            color: labelColor,
                            lineHeight: 1.2,
                            pt: 0.1,
                          }}
                        >
                          {milestone}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Sheet>
            )}
            {(linkedProjectWorkflowSummary.notes || linkedProjectWorkflowSummary.internalNotes) && (
              <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md' }}>
                {linkedProjectWorkflowSummary.notes && (
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                    <strong>Project notes:</strong> {linkedProjectWorkflowSummary.notes}
                  </Typography>
                )}
                {linkedProjectWorkflowSummary.internalNotes && (
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', mt: linkedProjectWorkflowSummary.notes ? 0.75 : 0 }}>
                    <strong>Internal notes:</strong> {linkedProjectWorkflowSummary.internalNotes}
                  </Typography>
                )}
              </Sheet>
            )}
            {canEditLinkedProject ? (
              <Card variant="outlined" sx={{ p: 1.25 }}>
                <Stack spacing={1.1}>
                  <Typography level="title-sm">Quick Edit (linked project only)</Typography>
                  <Grid container spacing={1}>
                    <Grid xs={12} md={6}>
                      <FormControl size="sm">
                        <FormLabel>Lifecycle status</FormLabel>
                        <Select
                          size="sm"
                          value={linkedProjectDraft?.lifecycle_status || 'active'}
                          onChange={(_, value) => {
                            if (!value) return;
                            updateLinkedProjectDraft({ lifecycle_status: value as LinkedProjectEditDraft['lifecycle_status'] });
                          }}
                          disabled={linkedProjectSaving || !linkedProjectDraft}
                        >
                          {PROJECT_LIFECYCLE_STATUS_OPTIONS.map((value) => (
                            <Option key={value} value={value}>{formatLifecycleStatusLabel(value)}</Option>
                          ))}
                        </Select>
                        <FormHelperText>Project lifecycle state (active/completed/cancelled).</FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl size="sm">
                        <FormLabel>Product Types</FormLabel>
                        <Select
                          multiple
                          size="sm"
                          value={linkedProjectDraft?.product_types || []}
                          onChange={(_, value) => {
                            const normalized = Array.isArray(value)
                              ? value.map((entry) => String(typeof entry === 'string' ? entry : (entry as any)?.value ?? entry))
                              : [];
                            updateLinkedProjectDraft({ product_types: normalized });
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {(selected || []).map((item, idx) => {
                                const label = String(typeof item === 'string' ? item : (item as any)?.value ?? item);
                                return <Chip key={`${label}-${idx}`} size="sm" variant="soft" color="primary">{label}</Chip>;
                              })}
                            </Box>
                          )}
                          disabled={linkedProjectSaving || !linkedProjectDraft}
                        >
                          {PRODUCT_TYPE_OPTIONS.map((value) => (
                            <Option key={value} value={value}>{value}</Option>
                          ))}
                        </Select>
                        <FormHelperText>Pick one or more product categories for this project.</FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                  {linkedProjectSaveError && (
                    <Alert color="danger" variant="soft">{linkedProjectSaveError}</Alert>
                  )}
                  {linkedProjectSaveSuccess && !linkedProjectSaveError && (
                    <Alert color="success" variant="soft">{linkedProjectSaveSuccess}</Alert>
                  )}
                </Stack>
              </Card>
            ) : (
              <Alert color="neutral" variant="soft">
                You have read-only access for linked project workflow and product fields.
              </Alert>
            )}
          </Stack>
        )}
      </Card>
      )}

      {/* Status Update — hidden in workspace embeds (status lives on Transmissions card) */}
      {!embedded && (
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>Status</Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Select 
            size="sm" 
            value={doc.status} 
            disabled={workspaceReadOnly}
            onChange={async (_, v) => {
              if (!v) return;
              const ok = window.confirm(`Change status to ${v}?`);
              if (!ok) return;
              
              try {
                const updated = await documentsService.updateDocument(doc.id, { status: v as any });
                setDoc((prev) => (prev ? { ...prev, ...updated, status: updated.status } : prev));

                if (
                  (doc.kind === 'Quotation' || doc.kind === 'Estimate')
                  && (v === 'accepted' || v === 'cash')
                ) {
                  onDocumentChanged?.(doc.id);
                }
                
                // Reload sync status after status change
                if (doc.kind === 'Invoice') {
                  if (['sent', 'paid', 'rejected'].includes(String(v))) {
                    try {
                      const eligibility = await getFreeAgentOrgEligibility(doc.organisationId);
                      if (!eligibility.eligible) {
                        alert(`Status updated, but the invoice was not sent to FreeAgent.\n\n${eligibility.reason}`);
                      }
                    } catch (err) {
                      console.error('Failed to check FreeAgent organisation eligibility:', err);
                    }
                  }
                  setTimeout(async () => {
                    try {
                      const status = await getSyncStatus(doc.id);
                      setSyncStatus(status);
                    } catch (err) {
                      console.error('Failed to reload sync status:', err);
                    }
                  }, 1000);
                }
                
                // Auto-create invoice when Estimate/Quote status changes to 'accepted'
                if ((doc.kind === 'Quotation' || doc.kind === 'Estimate') && v === 'accepted') {
                  try {
                    const created = await documentsService.ensureDepositInvoice(doc.id);
                    if (onDocumentChanged) {
                      onDocumentChanged(created?.id || doc.id);
                    } else if (created) {
                      alert(`Deposit invoice created. Invoice ID: ${created.id}`);
                    }
                  } catch (error: any) {
                    console.error('Error creating invoice:', error);
                    alert(`Failed to create invoice automatically: ${error.message}`);
                  }
                } else if (
                  (doc.kind === 'Quotation' || doc.kind === 'Estimate')
                  && (v === 'draft' || v === 'sent' || v === 'rejected')
                ) {
                  // Status rollback / non-acceptance changes: refresh parent docs but keep Final Survey.
                  onDocumentChanged?.(doc.id);
                }
              } catch (error: any) {
                console.error('Failed to update status:', error);
                alert(`Failed to update status: ${error.message}`);
              }
            }} 
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
          >
            {getAvailableStatuses(doc.kind).map(status => (
              <Option key={status} value={status}>{status}</Option>
            ))}
          </Select>
          
          {/* FreeAgent Sync Button (only for invoices) */}
          {doc.kind === 'Invoice' && !workspaceReadOnly && (
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {invoicePaymentTermsEditable(doc.invoiceRole) && !invoiceDatesLocked && (
                <Button size="sm" variant="outlined" onClick={() => setShowPaymentTerms(true)}>
                  Edit payment terms
                </Button>
              )}
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <FreeAgentSyncButton
                documentId={doc.id}
                organisationId={doc.organisationId}
                onSyncComplete={async () => {
                  // Reload sync status after sync
                  try {
                    const status = await getSyncStatus(doc.id);
                    setSyncStatus(status);
                  } catch (err) {
                    console.error('Failed to reload sync status:', err);
                  }
                  
                  // Reload document after FreeAgent sync. JARVIS Sent is customer-only.
                  try {
                    const updated = await documentsService.getDocument(doc.id);
                    setDoc(updated);
                  } catch (err) {
                    console.error('Failed to reload document after sync:', err);
                  }
                }}
              />
            </Box>
            </Stack>
          )}
        </Stack>
        {String(doc.status || '').toLowerCase() === 'accepted' && (
          <Typography level="body-sm" sx={{ mb: 1.25, color: 'text.secondary' }}>
            Accepted by customer:{' '}
            <b>
              {(() => {
                const ts = customerAcceptedAt || doc.acceptedAt || null;
                if (!ts) return 'Unknown';
                const ms = Date.parse(String(ts));
                return Number.isNaN(ms) ? String(ts) : new Date(ms).toLocaleString('en-GB');
              })()}
            </b>
          </Typography>
        )}
        {!embedded && transmissionVersionInfo?.hasMismatch && (
          <Alert color="warning" variant="soft" sx={{ mb: 1.25, alignItems: 'flex-start' }}>
            <Stack spacing={0.75} sx={{ width: '100%' }}>
              <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                The active version is not the latest version sent to the customer.
              </Typography>
              <Typography level="body-sm">
                Active version: {transmissionVersionInfo.activeVersionNumber != null ? `v${transmissionVersionInfo.activeVersionNumber}` : 'Unknown'}
                {' · '}
                Latest sent version: {transmissionVersionInfo.transmittedVersionNumber != null ? `v${transmissionVersionInfo.transmittedVersionNumber}` : 'Unknown'}
              </Typography>
              {transmissionVersionInfo.transmittedAt && (
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Last sent: {new Date(transmissionVersionInfo.transmittedAt).toLocaleString('en-GB')}
                </Typography>
              )}
            </Stack>
          </Alert>
        )}
        {doc.kind !== 'Invoice' && activeVersion?.header?.version_number != null && (
          <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Active version: v{Number((activeVersion as any).header.version_number)}
            </Typography>
            {!!(activeVersion as any).header.is_transmitted && (
              <Chip
                size="sm"
                variant="soft"
                color={(activeVersion as any).header.override_unlocked ? 'warning' : 'neutral'}
              >
                {(activeVersion as any).header.override_unlocked ? 'OVERRIDE OPEN' : 'IMMUTABLE'}
              </Chip>
            )}
          </Stack>
        )}

        {!embedded && (doc.kind === 'Estimate' || doc.kind === 'Quotation' || doc.kind === 'Invoice') && (
          <Stack spacing={1.25}>
            <Typography level="title-sm">Sent Public Links</Typography>
            <DocumentShareLinksList
              links={shareLinks}
              loading={loadingShareLinks}
              nullifyingId={nullifyingShareLinkId}
              onNullify={workspaceReadOnly ? undefined : (link) => {
                void (async () => {
                  const confirmed = window.confirm('NULLIFY this transmission link? This immediately blocks customer access for this URL only.');
                  if (!confirmed) return;
                  const reason = window.prompt('Optional nullify reason (internal audit):', '') || '';
                  try {
                    setNullifyingShareLinkId(link.id);
                    await nullifyDocumentShareLink(link.id, reason);
                    await refreshShareLinks(doc.id);
                  } catch (e: any) {
                    alert(`Failed to nullify link: ${e?.message || 'Unknown error'}`);
                  } finally {
                    setNullifyingShareLinkId(null);
                  }
                })();
              }}
            />
          </Stack>
        )}
        
        {/* FreeAgent Sync Status Display */}
        {doc.kind === 'Invoice' && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {freeAgentOrgReason && (
              <Alert color="warning" variant="soft">
                <Typography level="body-sm">{freeAgentOrgReason}</Typography>
              </Alert>
            )}
            {syncStatus && (
              <Stack spacing={1}>
                <Typography level="body-sm">
                  <strong>FreeAgent Sync:</strong> {syncStatus.syncStatus}
                </Typography>
                {syncStatus.freeAgentUrl && (
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    <a href={syncStatus.freeAgentUrl} target="_blank" rel="noopener noreferrer">
                      View in FreeAgent
                    </a>
                  </Typography>
                )}
                {syncStatus.syncError && (
                  <Typography level="body-sm" sx={{ color: 'danger' }}>
                    <strong>Error:</strong> {syncStatus.syncError}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}
        {doc.kind === 'Invoice' && (invoiceTotalsDrift || invoiceQuoteDrift?.paymentStructureMismatch) && (
          <Alert color="warning" variant="soft" sx={{ mt: 1 }}>
            {invoiceTotalsDrift && (
              <>
                <Typography level="body-sm">
                  <strong>Totals drift detected:</strong> stored invoice totals differ from line-based totals.
                </Typography>
                <Typography level="body-xs" sx={{ mt: 0.5 }}>
                  VAT delta {`£${invoiceTotalsDrift.delta.vat.toFixed(2)}`} • Net delta {`£${invoiceTotalsDrift.delta.net.toFixed(2)}`} • Gross delta {`£${invoiceTotalsDrift.delta.gross.toFixed(2)}`}
                </Typography>
              </>
            )}
            {invoiceQuoteDrift?.paymentStructureMismatch && (
              <Typography level="body-xs" sx={{ mt: invoiceTotalsDrift ? 0.5 : 0 }}>
                <strong>Payment structure drift detected:</strong> invoice `{invoiceQuoteDrift.invoicePaymentStructureApplicable ? (invoiceQuoteDrift.invoicePaymentStructurePattern || 'enabled') : 'disabled'}` vs quote `{invoiceQuoteDrift.quotePaymentStructureApplicable ? (invoiceQuoteDrift.quotePaymentStructurePattern || 'enabled') : 'disabled'}`.
              </Typography>
            )}
            {!doc.isOriginal && invoiceCanBeLifecycleSynced({
              invoiceRole: doc.invoiceRole,
              frozenAt: doc.frozenAt,
              status: doc.status,
              isOriginal: doc.isOriginal,
            }) && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="sm"
                  variant="outlined"
                  loading={isRegeneratingFromQuote}
                  disabled={isRegeneratingFromQuote || workspaceReadOnly}
                  onClick={async () => {
                    if (workspaceReadOnly) return;
                    const ok = window.confirm('Regenerate this invoice from the current quote data? This will overwrite invoice line/header values from the linked quote.');
                    if (!ok) return;
                    try {
                      setIsRegeneratingFromQuote(true);
                      await documentsService.syncInvoicesForLifecycle(doc.organisationId, doc.lifecycleRef);
                      const refreshed = await documentsService.getDocument(doc.id);
                      if (refreshed) setDoc(refreshed);
                      alert('Invoice regenerated from quote. If needed, use "Resync to FreeAgent" in this status card.');
                    } catch (err: any) {
                      console.error('Failed to regenerate invoice from quote:', err);
                      alert(`Failed to regenerate invoice: ${err?.message || 'Unknown error'}`);
                    } finally {
                      setIsRegeneratingFromQuote(false);
                    }
                  }}
                >
                  Regenerate Invoice from Quote
                </Button>
              </Box>
            )}
          </Alert>
        )}
        
        {viewDoc.kind === 'Invoice' && invoiceSchedule ? (
          <Stack spacing={0.5} sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-sm">
              <strong>Invoice date:</strong> {formatDateGb(invoiceSchedule.invoiceDate)}
            </Typography>
            <Typography level="body-sm">
              <strong>Payment terms:</strong> {invoiceSchedule.label}
            </Typography>
            <Typography level="body-sm">
              <strong>Due date:</strong> {formatDateGb(invoiceSchedule.dueDate)}
            </Typography>
            <Typography level="body-sm">{invoiceSchedule.wording}</Typography>
            {invoicePaymentTermsEditable(viewDoc.invoiceRole) && !invoiceDatesLocked && (
              <Box>
                <Button size="sm" variant="outlined" sx={{ mt: 0.5 }} onClick={() => setShowPaymentTerms(true)}>
                  Edit payment terms
                </Button>
              </Box>
            )}
          </Stack>
        ) : viewDoc.issuedAt ? (
          <Stack spacing={0.5} sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-sm">
              <strong>Document Date:</strong> {new Date(viewDoc.issuedAt).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </Typography>
          </Stack>
        ) : null}
      </Card>
      )}

      {!embedded && doc.kind !== 'Invoice' && (
        <Card sx={{ p: 2, mb: 3 }}>
          <VisualDesignerPairingCard
            readOnly
            versionNumber={Number(activeVersion?.header?.version_number) || null}
            documentNoun={doc.kind === 'Estimate' ? 'estimate' : 'quote'}
            pairing={{
              surveys: pairedSurvey ? [pairedSurvey] : [],
              selectedSurveyId: pairedSurvey?.id || null,
              selectedSurveyLabel: pairedSurvey?.label || (visualDesignerLinesUi.length > 0 ? 'Window designs on this version' : null),
              itemCount: visualDesignerLinesUi.length,
              pairingLocked: !!(activeVersion?.header?.is_transmitted && !activeVersion?.header?.override_unlocked),
              attachable: !!pairedSurvey,
              onSelectSurvey: () => undefined,
              onOpenVisualDesigner: () => {
                const projectId = String(doc.projectId || '').trim();
                if (projectId) navigate(`/projects/${projectId}/workspace/quotes`);
              },
            }}
          />
        </Card>
      )}

      {/* Customer Information */}
      {!embedded && (customerData || doc.customerFirstName || doc.customerLastName || doc.customerTitle) && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Customer</Typography>
          {(() => {
            // Derive a single, clean heading line with title + name
            const title = (documentIdentity.title || customerData?.title || doc.customerTitle || '').trim();
            const directoryName = (customerData?.name || '').trim();
            const docFullName = `${doc.customerFirstName || ''} ${doc.customerLastName || ''}`.trim();
            const displayName = (documentIdentity.fullName || directoryName || docFullName || 'Customer').trim();
            const heading = `${title ? title + ' ' : ''}${displayName}`.trim();

            const contactEmail = documentIdentity.email || customerData?.email || '';
            const contactPhone = documentIdentity.phone || customerData?.phone || customerData?.phone_number || '';
            const address = documentIdentity.address || customerData?.address || '';

            return (
              <Stack spacing={0.5}>
                <Typography level="body-sm">
                  {customerData ? (
                    <a 
                      href={`/customers/${encodeURIComponent(directoryName || docFullName || 'Customer')}`}
                      style={{ color: '#0b6bcb', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      {heading}
                    </a>
                  ) : (
                    heading
                  )}
                </Typography>
                {address && (
                  <Typography level="body-sm">{address}</Typography>
                )}
                {(contactEmail || contactPhone) && (
                  <Typography level="body-sm">
                    {contactEmail && (
                      <a 
                        href={`mailto:${contactEmail}`}
                        style={{ color: '#0b6bcb', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                      >
                        {contactEmail}
                      </a>
                    )}
                    {contactEmail && contactPhone && <span> · </span>}
                    {contactPhone && (
                      <a 
                        href={`tel:${contactPhone}`}
                        style={{ color: '#0b6bcb', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                      >
                        {contactPhone}
                      </a>
                    )}
                  </Typography>
                )}
              </Stack>
            );
          })()}
        </Card>
      )}

      {/* Internal Notes — hide on embedded invoices; deposit wording already lives on the document. */}
      {!(embedded && viewDoc.kind === 'Invoice') && String((viewDoc as any).notes || '').trim() && (
        <Card
          variant="soft"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#fff8db',
            border: '1px solid',
            borderColor: '#f1dc8f',
            boxShadow: 'sm',
          }}
        >
          <Typography level="title-sm" sx={{ mb: 1, color: '#7a5a00' }}>Internal Notes</Typography>
          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
            {String((viewDoc as any).notes || '').trim()}
          </Typography>
        </Card>
      )}

      {/* Description — always show in workspace embeds so expand isn't header → VAT only */}
      {(!!descriptionHtml || embedded) && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Description</Typography>
          {descriptionHtml ? (
            <Box
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.5,
                '& p': { my: 0, mb: 1.25 },
                '& p:last-of-type': { mb: 0 },
                '& ul, & ol': { my: 0, pl: 3 },
              }}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          ) : (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              No description yet. Use Edit to add one.
            </Typography>
          )}
        </Card>
      )}

      {/* Visual Designer — standalone only; workspace embeds use First/Final Survey tabs */}
      {!embedded && visualDesignerLinesUi.length > 0 && (
        <Card sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2, gap: 1 }}>
            <Typography level="title-sm">Visual Designer{pairedSurvey ? ` · ${pairedSurvey.label}` : ''}</Typography>
          </Stack>
          <Grid container spacing={2}>
            {visualDesignerLinesUi.map((line: any, idx: number) => {
              const meta = (line as any).metaJson || {};
              const style = meta?.window_style || {};
              const specs = meta?.specs || {};
              const options = specs?.options || {};
              const title = (specs?.reference || '').trim() || (style?.name || '').trim() || line.name || 'Window';
              const imageUrlExternal = toPublicImageUrl(style?.preview_path_external || style?.preview_path || style?.previewPath || '');
              const imageUrlInternal = toPublicImageUrl(style?.preview_path_internal || '');
              const imageUrlPresExternal = toPublicImageUrl(style?.preview_path_presentation_external || '');
              const imageUrlPresInternal = toPublicImageUrl(style?.preview_path_presentation_internal || '');
              const imageUrlDesignExternal = toPublicImageUrl(style?.preview_path_design_external || '');
              const imageUrlDesignInternal = toPublicImageUrl(style?.preview_path_design_internal || '');
              const isUploadedStyleMeta = String(style?.source || '').trim().toLowerCase() === 'upload';
              const uploadedImageSlotsForCard = [
                { label: 'Presentation · Outside', url: imageUrlPresExternal || toPublicImageUrl(style?.preview_path_external || '') },
                { label: 'Presentation · Inside', url: imageUrlPresInternal || toPublicImageUrl(style?.preview_path_internal || '') },
                { label: 'Design · Outside', url: imageUrlDesignExternal },
                { label: 'Design · Inside', url: imageUrlDesignInternal },
              ].filter((slot) => !!String(slot.url || '').trim());
              const templatePayload = style?.templatePayload as any;
              const isV2Lite = meta?.designer_version === 'v2_lite';
              let v2LitePayloadForCard: any = null;
              if (isV2Lite && templatePayload && templatePayload.root && templatePayload.w != null && templatePayload.h != null) {
                try {
                  const opts: any = options || {};
                  const toStr = (v: any) => (v == null ? '' : String(v));
                  const bayTangentWidthMm = Number(opts.bay_tangent_width_mm ?? 0) || 0;
                  v2LitePayloadForCard = (mergeModalStateIntoPayload as any)(templatePayload, {
                    frameColorCode: toStr((specs as any)?.frame_color_code || ''),
                    frameColorExtCode: toStr((specs as any)?.frame_color_code_external || ''),
                    frameColorIntCode: toStr((specs as any)?.frame_color_code_internal || ''),
                    frameColorExtName: toStr((specs as any)?.frame_color_external || ''),
                    frameColorIntName: toStr((specs as any)?.frame_color_internal || ''),
                    frameColorExtHex: toStr(opts.frame_color_hex_external || ''),
                    frameColorIntHex: toStr(opts.frame_color_hex_internal || ''),
                    preferExplicitFrameValues: true,
                    designDimsOn: opts.design_dims_on === true,
                    glassTypeCode: String((specs as any)?.glass_type_code || '').toLowerCase() === 'obscured' ? 'obscured' : 'clear',
                    widthMm: toStr((bayTangentWidthMm > 0 ? bayTangentWidthMm : specs.width_mm) ?? ''),
                    heightMm: toStr(specs.height_mm ?? ''),
                    handleColorCode: toStr(opts.handle_color_code || ''),
                    handleStyleCode: toStr(
                      opts.handle_style_code ||
                        (isDoorOnlyTypeId(meta?.window_type?.id) ? 'lever' : 'inline')
                    ),
                    trickleVents: opts.trickle_vents === true ? 'yes' : 'no',
                    trickleVentsPosition: parseSemanticTricklePosition(opts.trickle_vents_position),
                    cillOption: (opts.cill_option as any) || 'none',
                    extTopMm: toStr(opts.ext_top_mm ?? ''),
                    extBottomMm: toStr(opts.ext_bottom_mm ?? ''),
                    extLeftMm: toStr(opts.ext_left_mm ?? ''),
                    extRightMm: toStr(opts.ext_right_mm ?? ''),
                    georgianOn: opts.georgian_bars === true ? 'yes' : opts.georgian_bars === false ? 'no' : undefined,
                    georgianCols: toStr(opts.georgian_cols ?? ''),
                    georgianRows: toStr(opts.georgian_rows ?? ''),
                    georgianWidthMm: toStr(opts.georgian_width_mm ?? ''),
                    georgianOffXPct: toStr(opts.georgian_offset_x_pct ?? ''),
                    georgianOffYPct: toStr(opts.georgian_offset_y_pct ?? ''),
                    glassStyleTilePath: toStr(opts.glass_style_tile ?? ''),
                    astragalOn: opts.astragal_bars === true ? 'yes' : opts.astragal_bars === false ? 'no' : undefined,
                    astragalCols: toStr(opts.astragal_cols ?? ''),
                    astragalRows: toStr(opts.astragal_rows ?? ''),
                    astragalWidthMm: toStr(opts.astragal_width_mm ?? ''),
                    astragalOffXPct: toStr(opts.astragal_offset_x_pct ?? ''),
                    astragalOffYPct: toStr(opts.astragal_offset_y_pct ?? ''),
                    logicalDivsBackup: (opts.logical_divs && typeof opts.logical_divs === 'object') ? opts.logical_divs : null,
                    jointStyle: (opts.joint_style as any) || 'mitre-45',
                    isDoor: isDoorOnlyTypeId(meta?.window_type?.id),
                    forceNoOpenings: String(style?.category || '').trim().toLowerCase() === 'fixed',
                  }) as any;
                } catch {
                  v2LitePayloadForCard = null;
                }
              }
              const activeTemplatePayload =
                v2LitePayloadForCard && v2LitePayloadForCard.root && v2LitePayloadForCard.w != null && v2LitePayloadForCard.h != null
                  ? v2LitePayloadForCard
                  : templatePayload;
              const isBayPayloadForCard = isBayLikePayload(activeTemplatePayload);
              const windowType = (meta?.window_type?.name || '').trim();
              const material = (meta?.window_material?.name || '').trim();
              const rows = getDesignerSummaryRows(meta as any).filter((r) => !!String(r?.value || '').trim());

              return (
                <Grid key={line.id || idx} xs={12} md={12}>
                  <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                        <Box
                          sx={{
                            width: { xs: '100%', md: 320 },
                            maxWidth: { md: 360 },
                            flexShrink: 1, // image column gives up space first
                          }}
                        >
                          {isV2Lite && activeTemplatePayload && activeTemplatePayload.root && activeTemplatePayload.w != null && activeTemplatePayload.h != null ? (
                            <Grid container spacing={1}>
                              <Grid xs={12} sm={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'wg', payload: activeTemplatePayload, view: 'external', mode: 'presentation', title: `${title} · Presentation · Outside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& svg': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={520} view="external" mode="presentation" />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Presentation · Outside
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid xs={12} sm={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'wg', payload: activeTemplatePayload, view: 'internal', mode: 'presentation', title: `${title} · Presentation · Inside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& svg': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={520} view="internal" mode="presentation" />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Presentation · Inside
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid xs={12} sm={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'wg', payload: activeTemplatePayload, view: 'external', mode: 'design', title: `${title} · Design · Outside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& svg': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={520} view="external" mode="design" />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Design · Outside
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid xs={12} sm={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'wg', payload: activeTemplatePayload, view: 'internal', mode: 'design', title: `${title} · Design · Inside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& svg': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={520} view="internal" mode="design" />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Design · Inside
                                  </Typography>
                                </Box>
                              </Grid>
                              {isBayPayloadForCard && (
                                <Grid xs={12}>
                                  <Box sx={{ width: '100%' }}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'wg_bay_schematic', payload: activeTemplatePayload, title: `${title} · Bay Schematic` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 'sm',
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '10 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& svg': { width: '100%', height: '100%', display: 'block' },
                                      }}
                                    >
                                      <WindowGeneratorBaySchematicSvg payload={activeTemplatePayload} widthPx={520} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                      Bay Schematic
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          ) : isUploadedStyleMeta && uploadedImageSlotsForCard.length > 0 ? (
                            <Grid container spacing={1}>
                              {uploadedImageSlotsForCard.map((slot) => (
                                <Grid key={slot.label} xs={6}>
                                  <Box sx={{ width: '100%' }}>
                                    <Box
                                      onClick={() => setZoomPreview({ kind: 'img', url: slot.url, title: `${title} · ${slot.label}` })}
                                      sx={{
                                        cursor: 'zoom-in',
                                        borderRadius: 'sm',
                                        overflow: 'hidden',
                                        bgcolor: '#d9dee7',
                                        aspectRatio: '8 / 7',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& canvas': { width: '100%', height: '100%', display: 'block' },
                                      }}
                                    >
                                      <ImageCanvasViewer imageUrl={slot.url} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                      {slot.label}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          ) : (imageUrlPresExternal && imageUrlPresInternal && imageUrlDesignExternal && imageUrlDesignInternal) ? (
                            <Grid container spacing={1}>
                              <Grid xs={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'img', url: imageUrlPresExternal, title: `${title} · Presentation · Outside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& canvas': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <ImageCanvasViewer imageUrl={imageUrlPresExternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Presentation · Outside
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid xs={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'img', url: imageUrlPresInternal, title: `${title} · Presentation · Inside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& canvas': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <ImageCanvasViewer imageUrl={imageUrlPresInternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Presentation · Inside
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid xs={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'img', url: imageUrlDesignExternal, title: `${title} · Design · Outside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& canvas': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <ImageCanvasViewer imageUrl={imageUrlDesignExternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Design · Outside
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid xs={6}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    onClick={() => setZoomPreview({ kind: 'img', url: imageUrlDesignInternal, title: `${title} · Design · Inside` })}
                                    sx={{
                                      cursor: 'zoom-in',
                                      borderRadius: 'sm',
                                      overflow: 'hidden',
                                      bgcolor: '#d9dee7',
                                      aspectRatio: '8 / 7',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& canvas': { width: '100%', height: '100%', display: 'block' },
                                    }}
                                  >
                                    <ImageCanvasViewer imageUrl={imageUrlDesignInternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                    Design · Inside
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          ) : (imageUrlExternal && imageUrlInternal ? (
                            <Stack spacing={0.75}>
                              <Box sx={{ width: '100%' }}>
                                <Box
                                  onClick={() => setZoomPreview({ kind: 'img', url: imageUrlExternal, title: `${title} · Outside` })}
                                  sx={{
                                    cursor: 'zoom-in',
                                    borderRadius: 'sm',
                                    overflow: 'hidden',
                                    bgcolor: '#d9dee7',
                                    aspectRatio: '8 / 7',
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '& canvas': { width: '100%', height: '100%', display: 'block' },
                                  }}
                                >
                                  <ImageCanvasViewer imageUrl={imageUrlExternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                </Box>
                                <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                  Viewed from outside
                                </Typography>
                              </Box>
                              <Box sx={{ width: '100%' }}>
                                <Box
                                  onClick={() => setZoomPreview({ kind: 'img', url: imageUrlInternal, title: `${title} · Inside` })}
                                  sx={{
                                    cursor: 'zoom-in',
                                    borderRadius: 'sm',
                                    overflow: 'hidden',
                                    bgcolor: '#d9dee7',
                                    aspectRatio: '8 / 7',
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '& canvas': { width: '100%', height: '100%', display: 'block' },
                                  }}
                                >
                                  <ImageCanvasViewer imageUrl={imageUrlInternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                                </Box>
                                <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
                                  Viewed from inside
                                </Typography>
                              </Box>
                            </Stack>
                          ) : style?.templatePayload ? (
                            <Box sx={{ width: '100%' }}>
                              <WindowGeneratorThumbnail payload={style.templatePayload} heightPx={180} />
                            </Box>
                          ) : imageUrlExternal ? (
                            <Box sx={{ width: '100%' }}>
                              <ImageCanvasViewer imageUrl={imageUrlExternal} overallWidthMm={null} overallHeightMm={null} heightPx={180} />
                            </Box>
                          ) : (
                            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
                              <Typography level="body-sm">No image available</Typography>
                            </Sheet>
                          ))}
                        </Box>

                        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                          <Typography level="title-md">{title}</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                            {windowType && (
                              <Chip size="sm" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
                                {windowType}
                              </Chip>
                            )}
                            {material && (
                              <Chip size="sm" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
                                {material}
                              </Chip>
                            )}
                            <Chip size="sm" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
                              Qty: {line.qty ?? 1}
                            </Chip>
                          </Box>

                          {rows.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Table size="sm">
                                <tbody>
                                  {rows.map((r) => (
                                    <tr key={r.label}>
                                      <td style={{ width: 190, minWidth: 190, whiteSpace: 'nowrap', verticalAlign: 'top', paddingRight: 22 }}>
                                        <Typography level="body-xs" fontWeight="lg">{r.label}</Typography>
                                      </td>
                                      <td style={{ verticalAlign: 'top', paddingLeft: 10 }}>
                                        <Typography level="body-xs" sx={{ wordBreak: 'break-word' }}>{r.value}</Typography>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      )}

      <Modal open={!!zoomPreview} onClose={() => setZoomPreview(null)}>
        <ModalDialog layout="center" sx={{ p: 2, maxWidth: '95vw', maxHeight: '95vh' }}>
          <DialogTitle sx={{ pr: 5 }}>{zoomPreview?.title || 'Preview'}</DialogTitle>
          <DialogContent sx={{ overflow: 'auto' }}>
            {zoomPreview?.kind === 'wg' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', '& svg': { width: '100%', height: 'auto' } }}>
                <WindowGeneratorSvg
                  payload={zoomPreview.payload}
                  widthPx={980}
                  view={zoomPreview.view}
                  mode={zoomPreview.mode}
                />
              </Box>
            ) : zoomPreview?.kind === 'wg_bay_schematic' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', '& svg': { width: '100%', height: 'auto' } }}>
                <WindowGeneratorBaySchematicSvg payload={zoomPreview.payload} widthPx={980} />
              </Box>
            ) : zoomPreview?.kind === 'img' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img src={zoomPreview.url} alt={zoomPreview.title} style={{ maxWidth: '92vw', maxHeight: '80vh', width: 'auto', height: 'auto' }} />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setZoomPreview(null)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Line Items — keep the section visible in workspace embeds even when empty */}
      {(pricingLinesUi.length > 0 || embedded) && (
        <Card sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 2 }}>Line Items</Typography>
          {pricingLinesUi.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              No line items yet. Use Edit to add materials, labour, or other costs.
            </Typography>
          ) : (
          <>
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto', minWidth: 600 }}>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '15%', minWidth: '120px' }}>Type</th>
                  <th style={{ width: '30%', minWidth: '200px' }}>Item</th>
                  <th style={{ width: '10%', minWidth: '80px' }}>Qty</th>
                  <th style={{ width: '10%', minWidth: '80px' }}>Unit</th>
                  <th style={{ width: '15%', minWidth: '100px' }}>Unit Price</th>
                  <th style={{ width: '10%', minWidth: '90px' }}>VAT Inclusive</th>
                  <th style={{ width: '10%', minWidth: '100px' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {pricingLinesUi.map((line: any, index: number) => (
                  <tr key={line.id || index}>
                    <td>{line.lineKind === 'other' ? getAdditionalTypeLabel(line.additionalType) : '—'}</td>
                    <td>
                      <Typography level="body-sm" fontWeight="bold">{line.name}</Typography>
                      {(line.description || depositNarrative) && (
                        <Typography level="body-xs" sx={{ color: 'neutral.500', whiteSpace: 'pre-line' }}>{line.description || depositNarrative}</Typography>
                      )}
                    </td>
                    <td>{line.qty}</td>
                    <td>{line.unit}</td>
                    <td>{money(line.unitPrice)}</td>
                    <td>{line.vatInclusive ? 'Yes' : 'No'}</td>
                    <td>{money(line.lineTotal || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Stack spacing={2}>
              {pricingLinesUi.map((line: any, index: number) => (
                <Card key={line.id || index} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        Type: {line.lineKind === 'other' ? getAdditionalTypeLabel(line.additionalType) : '—'}
                      </Typography>
                      <Typography level="body-sm" fontWeight="bold">{line.name}</Typography>
                      {(line.description || depositNarrative) && (
                        <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5, whiteSpace: 'pre-line' }}>{line.description || depositNarrative}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Qty</Typography>
                        <Typography level="body-sm">{line.qty}</Typography>
                      </Box>
                      {line.unit && (
                        <Box>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Unit</Typography>
                          <Typography level="body-sm">{line.unit}</Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Unit Price</Typography>
                        <Typography level="body-sm">{money(line.unitPrice)}</Typography>
                      </Box>
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>VAT Inclusive</Typography>
                        <Typography level="body-sm">{line.vatInclusive ? 'Yes' : 'No'}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Total</Typography>
                        <Typography level="body-sm" fontWeight="lg">{money(line.lineTotal || 0)}</Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
          </>
          )}
        </Card>
      )}

      {/* Project Duration Display */}
      {(viewDoc.projectDays || viewDoc.projectHours) && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 2 }}>Project Duration</Typography>
          {viewDoc.projectDays && (
            <Typography level="body-sm">
              <strong>Specified Duration:</strong> {viewDoc.projectDays} day{viewDoc.projectDays === 1 ? '' : 's'}
            </Typography>
          )}
          {viewDoc.projectHours && (
            <Typography level="body-sm">
              <strong>Specified Duration:</strong> {viewDoc.projectHours} hour{viewDoc.projectHours === 1 ? '' : 's'}
            </Typography>
          )}
        </Card>
      )}

      {/* Deposit */}
      {viewDoc.hasDeposit && viewDoc.depositAmount > 0 && !invoicePdfUsesOwnTotals(invoiceRole) && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Deposit</Typography>
          <Stack spacing={0.5}>
            <Typography level="body-sm" sx={{ color: 'neutral.900' }}>
              <strong>Deposit Amount (incl. VAT):</strong> {money(depositGross)}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Deposit Net (excl. VAT): {money(depositNet)}
            </Typography>
            {viewDoc.vatEnabled && (
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Deposit VAT: {money(depositVatPortion)}
              </Typography>
            )}
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Deposit Percentage:{' '}
              {viewDoc.depositPercentage != null
                ? `${(viewDoc.depositPercentage * 100).toFixed(0)}%`
                : 'Fixed amount'}
            </Typography>
            {depositNarrative && (
              <Typography level="body-sm" sx={{ color: 'neutral.700', whiteSpace: 'pre-line', mt: 0.75 }}>
                {depositNarrative}
              </Typography>
            )}
            {showOutstandingBreakdown && viewDoc.totals?.grandTotal != null && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography level="body-sm" sx={{ color: 'neutral.900' }}>
                  <strong>Outstanding Balance (incl. VAT):</strong> {money(outstandingGross)}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Outstanding Net (excl. VAT): {money(outstandingNet)}
                </Typography>
                {viewDoc.vatEnabled && (
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Outstanding VAT: {money(outstandingVatPortion)}
                  </Typography>
                )}
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Outstanding Percentage:{' '}
                  {viewDoc.depositPercentage != null
                    ? `${(Math.max(0, 1 - viewDoc.depositPercentage) * 100).toFixed(0)}%`
                    : 'N/A'}
                </Typography>
              </>
            )}
            {showOutstandingBreakdown && activePaymentStructure && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography level="body-sm" sx={{ color: 'neutral.900' }}>
                  <strong>Payment Structure:</strong> {activePaymentStructure.label}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  Staged payments are based on Total incl. VAT, with remaining stages applied to the balance after the deposit. This does not alter the invoice totals.
                </Typography>
                <Typography level="body-xs">
                  Deposit: {activePaymentStructure.parts[0]}% → {money(r2(grandTotal * (activePaymentStructure.parts[0] / 100)))}
                </Typography>
                {(() => {
                  const remainingParts = activePaymentStructure.parts.slice(1);
                  const sumRemaining = remainingParts.reduce((acc, p) => acc + p, 0) || 1;
                  return remainingParts.map((p, idx) => {
                    const share = p / sumRemaining;
                    const amount = r2(remainingAfterDeposit * share);
                    return (
                      <Typography key={idx} level="body-xs">
                        Stage {idx + 1}: £{money(amount).slice(1)}
                      </Typography>
                    );
                  });
                })()}
              </>
            )}
          </Stack>
        </Card>
      )}

      {/* VAT Settings */}
      <Card sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>VAT Settings</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Chip size="sm" variant={viewDoc.vatEnabled ? "solid" : "outlined"}>
            VAT {viewDoc.vatEnabled ? 'Enabled' : 'Disabled'} ({percent(viewDoc.vatPct)})
          </Chip>
          {viewDoc.inputVatSelected && (
            <Chip size="sm" variant="solid" color="warning">
              Input VAT Selected
            </Chip>
          )}
        </Stack>
      </Card>

      {/* Totals */}
      <Card sx={{ p: 2 }}>
        <Typography level="title-sm" sx={{ mb: 2 }}>Totals</Typography>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Base Cost Total</Typography>
            <Typography level="body-sm">{money(totals.baseCost)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Admin Fee ({percent(viewDoc.fees.adminPct)})</Typography>
            <Typography level="body-sm">{money(totals.adminFee)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Contingency ({percent(viewDoc.fees.contingencyPct)})</Typography>
            <Typography level="body-sm">{money(totals.contingency)}</Typography>
          </Box>
          {/* Keep original Profit margin row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Profit Margin ({percent(viewDoc.fees.profitPct)})</Typography>
            <Typography level="body-sm">{money(totals.profit)}</Typography>
          </Box>
          {/* New line: Profit (after labour deducted) */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Profit (after labour deducted)</Typography>
            <Typography level="body-sm">{money(profitDisplay)}</Typography>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Base + Company Additions (pre VAT)</Typography>
            <Typography level="body-sm">{money(netTotal)}</Typography>
          </Box>
          {viewDoc.vatEnabled && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm" fontWeight="bold">VAT ({percent(viewDoc.vatPct)})</Typography>
                <Typography level="body-sm" fontWeight="bold">{money(vatAmount)}</Typography>
              </Box>
              <Divider />
            </>
          )}
          {viewDoc.vatEnabled && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography level="body-sm">Total (excl. VAT)</Typography>
              <Typography level="body-sm">{money(netTotal)}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm" fontWeight="bold">Total{viewDoc.vatEnabled ? ' (incl. VAT)' : ''}</Typography>
            <Typography level="body-sm" fontWeight="bold">{money(grandTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">Profit (- all overheads)</Typography>
            <Typography level="body-sm">{money(profitDisplay)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm">{profitPerDayLabel}</Typography>
            <Typography level="body-sm">{profitPerDay == null ? 'please populate project duration' : money(profitPerDay)}</Typography>
          </Box>
          {viewDoc.inputVatSelected && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm">Input VAT (goods only)</Typography>
                <Typography level="body-sm">{money(viewDoc.vatEnabled ? viewDoc.lines
                  .filter((l: any) => l.lineKind === 'material' || l.lineKind === 'equipment' || l.lineKind === 'other')
                  .reduce((acc: any, l: any) => {
                    const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
                    const gross = r2((l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0)));
                    const isInclusive = (l as any).vatInclusive ? true : false;
                    const vatPart = isInclusive ? (gross * (viewDoc.vatPct / (1 + viewDoc.vatPct))) : (gross * viewDoc.vatPct);
                    return acc + vatPart;
                  }, 0) : 0)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm">Output VAT ({percent(viewDoc.vatPct)})</Typography>
                <Typography level="body-sm">{money(vatAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm" fontWeight="bold">Net VAT ({(() => { const gv = (viewDoc.vatEnabled ? viewDoc.lines
                  .filter((l: any) => l.lineKind === 'material' || l.lineKind === 'equipment' || l.lineKind === 'other')
                  .reduce((acc: any, l: any) => { const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100; const gross = r2((l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0))); const isInclusive = (l as any).vatInclusive ? true : false; const vatPart = isInclusive ? (gross * (viewDoc.vatPct / (1 + viewDoc.vatPct))) : (gross * viewDoc.vatPct); return acc + vatPart; }, 0) : 0); return vatAmount - gv >= 0 ? 'payable' : 'repayable'; })()})</Typography>
                <Typography level="body-sm" fontWeight="bold">{(() => { const gv = (viewDoc.vatEnabled ? viewDoc.lines
                  .filter((l: any) => l.lineKind === 'material' || l.lineKind === 'equipment' || l.lineKind === 'other')
                  .reduce((acc: any, l: any) => { const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100; const gross = r2((l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0))); const isInclusive = (l as any).vatInclusive ? true : false; const vatPart = isInclusive ? (gross * (viewDoc.vatPct / (1 + viewDoc.vatPct))) : (gross * viewDoc.vatPct); return acc + vatPart; }, 0) : 0); return money(Math.abs(vatAmount - gv)); })()}</Typography>
              </Box>
            </>
          )}
        </Stack>
      </Card>

      {!embedded && !workspaceReadOnly && (
      <Card variant="outlined" sx={{ p: 1.25, mt: 3, mb: 3 }}>
        <Stack spacing={0.9}>
          <Typography level="title-sm">Email Automation & Events</Typography>
          {!String(doc.projectId || '').trim() ? (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Link this document to a project to manage automation events.
            </Typography>
          ) : linkedProjectLoading && !linkedProject ? (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Loading automation status…
            </Typography>
          ) : linkedProjectError && !linkedProject ? (
            <Alert color="danger" variant="soft">{linkedProjectError}</Alert>
          ) : !linkedProject ? (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Linked project not found.
            </Typography>
          ) : !customerForEventManager ? (
            <Alert color="warning" variant="soft">
              Customer details are required to manage email automations for this project.
            </Alert>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} sx={{ flexWrap: 'wrap', alignItems: { sm: 'center' } }}>
                <Chip size="sm" variant="soft" color="neutral">
                  Status: {Object.keys(linkedProjectWorkflowSummary?.emailStatusSummary || {}).length === 0
                    ? 'No email events'
                    : Object.entries(linkedProjectWorkflowSummary?.emailStatusSummary || {})
                        .map(([status, count]) => `${status} ${count}`)
                        .join(' · ')}
                </Chip>
                {linkedProjectWorkflowSummary?.lastEmailEvent && (
                  <Chip size="sm" variant="outlined" color="neutral">
                    Last: {String(linkedProjectWorkflowSummary.lastEmailEvent.event_type || 'event')} · {formatDateTimeLabel(
                      linkedProjectWorkflowSummary.lastEmailEvent.email_sent_at
                      || linkedProjectWorkflowSummary.lastEmailEvent.event_date
                      || linkedProjectWorkflowSummary.lastEmailEvent.created_at
                    )}
                  </Chip>
                )}
              </Stack>
              <Box
                component="details"
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  pt: 0.8,
                  '& > summary': {
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'primary.600',
                    listStyle: 'none',
                  },
                  '& > summary::-webkit-details-marker': { display: 'none' },
                }}
              >
                <summary>Open automation controls</summary>
                <Box sx={{ mt: 0.8 }}>
                  <EventManager
                    projectId={linkedProject.id}
                    customer={customerForEventManager}
                  />
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </Card>
      )}

      {/* Print-friendly version (hidden by default) */}
      <Box id="document-print-area" sx={{ display: 'none' }}>
        <div className="hdr">
          <div>
            <div className="title">{config?.headerTitle || viewDoc.title}</div>
            <div className="subtitle">{viewDoc.kind.charAt(0).toUpperCase() + viewDoc.kind.slice(1)} - {documentDisplayRef(viewDoc)}</div>
            <div className="muted">Date: {new Date(viewDoc.createdAt).toLocaleDateString('en-GB')}</div>
          </div>
          <div>
            {config?.logoPath ? (
              <img src={config.logoPath} alt="Company Logo" className="logo" />
            ) : (
              <span className={`status-chip status-${viewDoc.status}`}>{viewDoc.status}</span>
            )}
          </div>
        </div>

        <div className="info-grid">
          <div className="info-section">
            <h3>From</h3>
            <p>{config?.companyName || ''}<br />
            {config?.companyAddress || 'Address details would go here'}<br />
            {config?.companyPhone && `Phone: ${config.companyPhone}`}<br />
            {config?.companyEmail && `Email: ${config.companyEmail}`}<br />
            {config?.companyVatNumber && `VAT: ${config.companyVatNumber}`}</p>
          </div>
          <div className="info-section">
            <h3>To</h3>
            <p>{viewDoc.customerFirstName && viewDoc.customerLastName ? `${viewDoc.customerFirstName} ${viewDoc.customerLastName}` : 'Customer details'}<br />
            Customer address would go here</p>
          </div>
        </div>

        {(depositNarrative || viewDoc.description) && (
          <div style={{ margin: '24px 0' }}>
            <h3>Description</h3>
            <div dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
          </div>
        )}

        {viewDoc.lines.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {viewDoc.lines.map((line: any, index: number) => (
                <tr key={line.id || index}>
                  <td>
                    <strong>{line.name}</strong>
                    {(line.description || depositNarrative) && <br />}
                    {(line.description || depositNarrative) && <span className="muted">{line.description || depositNarrative}</span>}
                  </td>
                  <td>{line.qty}</td>
                  <td>{line.unit}</td>
                  <td>{money(line.unitPrice)}</td>
                  <td>{money(line.lineTotal || 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Base Cost Total</td>
                <td>{money(totals.baseCost)}</td>
              </tr>
              <tr>
                <td colSpan={4}>Admin Fee ({percent(viewDoc.fees.adminPct)})</td>
                <td>{money(totals.adminFee)}</td>
              </tr>
              <tr>
                <td colSpan={4}>Contingency ({percent(viewDoc.fees.contingencyPct)})</td>
                <td>{money(totals.contingency)}</td>
              </tr>
              <tr>
                <td colSpan={4}>Profit (after labour deducted) ({percent(viewDoc.fees.profitPct)})</td>
                <td>{money(totals.profit)}</td>
              </tr>
              <tr>
                <td colSpan={4}>Base + Company Additions (pre VAT)</td>
                <td>{money(netTotal)}</td>
              </tr>
              {viewDoc.vatEnabled && (
                <tr>
                  <td colSpan={4}>VAT ({percent(viewDoc.vatPct)})</td>
                  <td>{money(vatAmount)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={4}><strong>Total{viewDoc.vatEnabled ? ' (incl. VAT)' : ''}</strong></td>
                <td><strong>{money(grandTotal)}</strong></td>
              </tr>
            </tfoot>
          </table>
        )}

        {viewDoc.hasDeposit && viewDoc.depositAmount > 0 && !invoicePdfUsesOwnTotals(invoiceRole) && (
          <div style={{ margin: '24px 0', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h3>Deposit</h3>
            <p><strong>{money(viewDoc.depositAmount)}</strong></p>
            {viewDoc.depositPercentage && (
              <p className="muted">{viewDoc.depositPercentage ? `${(viewDoc.depositPercentage * 100).toFixed(0)}%` : 'Fixed Amount'}</p>
            )}
          </div>
        )}
      </Box>

      <Modal open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <DialogTitle>{confirmDialog?.title}</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.600', whiteSpace: 'pre-line' }}>
            {confirmDialog?.body}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
            <Button variant="plain" color="neutral" onClick={() => setConfirmDialog(null)}>
              Cancel
            </Button>
            <Button
              color={confirmDialog?.confirmColor || 'primary'}
              onClick={() => {
                const action = confirmDialog?.onConfirm;
                setConfirmDialog(null);
                if (action) void action();
              }}
            >
              {confirmDialog?.confirmLabel || 'OK'}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={!!noticeDialog} onClose={() => setNoticeDialog(null)}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <DialogTitle>{noticeDialog?.title}</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.600', whiteSpace: 'pre-line' }}>
            {noticeDialog?.body}
          </Typography>
          {noticeDialog?.url ? (
            <Link href={noticeDialog.url} target="_blank" rel="noopener noreferrer" level="body-sm" sx={{ mt: 1, fontWeight: 600 }}>
              Open customer link
            </Link>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
            <Button onClick={() => setNoticeDialog(null)}>OK</Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Snapshot Modal */}
      <Modal open={snapshotOpen} onClose={() => setSnapshotOpen(false)}>
        <ModalDialog sx={{ maxWidth: 560 }}>
          <DialogTitle>{`${(`${viewDoc?.customerTitle ? viewDoc.customerTitle + ' ' : ''}${viewDoc?.customerFirstName || ''} ${viewDoc?.customerLastName || ''}`).trim() || 'Document'} - Snapshot`}</DialogTitle>
          <DialogContent>
            {viewDoc && (
              <Box sx={{ fontSize: '0.9rem' }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1, rowGap: 1 }}>
                  <Box>
                    <Typography level="body-sm" fontWeight="lg">Overview</Typography>
                    <Table size="sm">
                      <tbody>
                        <tr><td>Kind</td><td>{viewDoc.kind}</td></tr>
                        <tr><td>Reference</td><td>{documentDisplayRef(viewDoc)}</td></tr>
                        <tr><td>Status</td><td>{viewDoc.status}</td></tr>
                        <tr><td>Title</td><td>{viewDoc.title}</td></tr>
                        <tr><td>Customer</td><td>{`${viewDoc.customerTitle ? viewDoc.customerTitle + ' ' : ''}${viewDoc.customerFirstName || ''} ${viewDoc.customerLastName || ''}`.trim() || '—'}</td></tr>
                        {viewDoc.kind === 'Invoice' && invoiceSchedule ? (
                          <>
                            <tr><td>Invoice date</td><td>{formatDateGb(invoiceSchedule.invoiceDate)}</td></tr>
                            <tr><td>Payment terms</td><td>{invoiceSchedule.label}</td></tr>
                            <tr><td>Due date</td><td>{formatDateGb(invoiceSchedule.dueDate)}</td></tr>
                          </>
                        ) : viewDoc.issuedAt ? (
                          <tr><td>Document Date</td><td>{new Date(viewDoc.issuedAt).toLocaleDateString('en-GB', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}</td></tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </Box>
                  <Box>
                    <Typography level="body-sm" fontWeight="lg">Totals</Typography>
                    {(() => {
                      const totals = computeTotals(
                        (viewDoc.lines || []).map((l: any) => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal, vatInclusive: l.vatInclusive })),
                        viewDoc.fees,
                        { enabled: viewDoc.vatEnabled, pct: viewDoc.vatPct }
                      );
                      const money = (n: number) => `£${(n || 0).toFixed(2)}`;
                      const adminPct = Math.round((viewDoc.fees?.adminPct ?? 0) * 100);
                      const contPct = Math.round((viewDoc.fees?.contingencyPct ?? 0) * 100);
                      const profitPct = Math.round((viewDoc.fees?.profitPct ?? 0) * 100);
                      return (
                        <Table size="sm">
                          <tbody>
                            <tr><td>Base Cost</td><td>{money(totals.baseCost)}</td></tr>
                            <tr><td>{`Admin Fee (${adminPct}%)`}</td><td>{money(totals.adminFee)}</td></tr>
                            <tr><td>{`Contingency (${contPct}%)`}</td><td>{money(totals.contingency)}</td></tr>
                            <tr><td>Profit Margin ({profitPct}%)</td><td>{money(totals.profit)}</td></tr>
                            <tr><td>Profit (after labour deducted)</td><td>{money(totals.adminFee + totals.contingency + (totals.baseCost * (viewDoc.fees.profitPct ?? 0)))}</td></tr>
                            <tr><td>Total{viewDoc.vatEnabled ? ' (incl. VAT)' : ''}</td><td><strong>{money(totals.grandTotal)}</strong></td></tr>
                          </tbody>
                        </Table>
                      );
                    })()}
                  </Box>
                </Box>

                {/* Line Items */}
                {Array.isArray(viewDoc.lines) && viewDoc.lines.length > 0 && (
                  <>
                    <Divider />
                    <Typography level="body-sm" fontWeight="lg">Line Items</Typography>
                    {(['material','equipment','labour','other'] as const).map(kindKey => {
                      const items = (viewDoc.lines || []).filter((l: any) => l.lineKind === kindKey);
                      if (items.length === 0) return null;
                      return (
                        <Box key={kindKey} sx={{ p: 0.5, mb: 0.5, border: '1px solid var(--joy-palette-neutral-300)', borderRadius: '4px' }}>
                          <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                            {kindKey === 'other' ? 'Additional' : (kindKey.charAt(0).toUpperCase() + kindKey.slice(1))}
                          </Typography>
                          <Table size="sm">
                            <thead>
                              <tr><th>Type</th><th style={{textAlign:'left'}}>Name</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>VAT Inclusive</th><th>Total</th></tr>
                            </thead>
                            <tbody>
                              {items.map((l: any, i: number) => (
                                <tr key={i}>
                                  <td>{kindKey === 'other' ? getAdditionalTypeLabel(l.additional_type ?? l.additionalType ?? null) : '—'}</td>
                                  <td style={{textAlign:'left'}}>{l.name || '—'}</td>
                                  <td>{l.qty}</td>
                                  <td>{l.unit || '—'}</td>
                                  <td>£{(l.unitPrice || 0).toFixed(2)}</td>
                                  <td>{l.vatInclusive ? 'Yes' : 'No'}</td>
                                  <td>£{(l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0)).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Box>
                      );
                    })}
                  </>
                )}

                {(viewDoc.projectDays || viewDoc.projectHours) && (
                  <>
                    <Divider />
                    <Typography level="body-sm" fontWeight="lg">Project Duration</Typography>
                    <Typography level="body-xs">{viewDoc.projectDays ? `${viewDoc.projectDays} day(s)` : `${viewDoc.projectHours} hour(s)`}</Typography>
                  </>
                )}

                {(viewDoc.hasDeposit && (viewDoc.depositAmount || 0) > 0 && !invoicePdfUsesOwnTotals(invoiceRole)) && (
                  <>
                    <Divider />
                    <Typography level="body-sm" fontWeight="lg">Deposit</Typography>
                    <Typography level="body-xs">Amount: £{(viewDoc.depositAmount || 0).toFixed(2)}</Typography>
                    {viewDoc.depositPercentage != null && (
                      <Typography level="body-xs">Percentage: {(viewDoc.depositPercentage * 100).toFixed(0)}%</Typography>
                    )}
                  </>
                )}
              </Stack>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setSnapshotOpen(false)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Document Date Picker Modal */}
      <DocumentDatePickerModal
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onConfirm={handleDateConfirm}
        defaultDate={invoiceSchedule ? dateOnlyToIssuedAt(invoiceSchedule.invoiceDate) : (viewDoc.issuedAt || viewDoc.createdAt)}
        dateLocked={viewDoc.kind === 'Invoice' && invoiceDatesLocked}
        documentTitle={viewDoc.kind}
        showInvoiceScenario={viewDoc.kind === 'Invoice'}
        defaultInvoiceScenario={viewDoc.kind === 'Invoice' ? invoicePdfScenario : undefined}
        invoiceRole={viewDoc.kind === 'Invoice' ? viewDoc.invoiceRole : undefined}
        invoiceStageOptions={viewDoc.kind === 'Invoice' ? invoiceStageOptions : undefined}
        defaultInvoiceStageIndex={viewDoc.kind === 'Invoice' ? invoicePdfStageIndex : undefined}
      />
      {viewDoc.kind === 'Invoice' && invoicePaymentTermsEditable(viewDoc.invoiceRole) && (
        <InvoicePaymentTermsModal
          open={showPaymentTerms}
          locked={invoiceDatesLocked}
          invoiceRole={viewDoc.invoiceRole}
          issuedAt={viewDoc.issuedAt}
          createdAt={viewDoc.createdAt}
          paymentTermsInDays={viewDoc.paymentTermsInDays}
          onClose={() => setShowPaymentTerms(false)}
          onSave={async ({ invoiceDate, paymentTermsInDays }) => {
            const updated = await documentsService.updateDocument(viewDoc.id, {
              issuedAt: dateOnlyToIssuedAt(invoiceDate),
              paymentTermsInDays,
            });
            setDoc((prev) => (prev ? { ...prev, ...updated } : prev));
          }}
        />
      )}
    </Box>
  );
};

export default DocumentDetailPage;




